// src/pages/FinanzasUsuario.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import {
    FaMoneyCheckAlt,
    FaInfoCircle,
    FaCalendarAlt,
    FaPercentage,
    FaDollarSign,
    FaExclamationCircle,
    FaHome,
    FaUser,
    FaWallet,
    FaChevronDown,
    FaChevronUp,
    FaArrowRight,
    FaPlusCircle,
    FaBolt,
    FaHistory,
    FaTimesCircle,
    FaClock,
    FaReceipt,
    FaGavel,
    FaWater,
    FaListUl,
    FaChevronRight,
    FaTimes,
    FaEye,
    FaUpload,
    FaPaperclip,
    FaSpinner
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { storage } from '../firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { registrarComprobantePago } from '../services/domusAiService';
import { calcularAdeudoVisibleDesdeObjeto } from '../utils/calcularAdeudoVisible';
import { calcularEstadoMoroso } from '../utils/morosoUtils';
import { useModulos } from '../hooks/useModulos';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import ModalAvisoMorosidadFinanzas from '../components/ModalAvisoMorosidadFinanzas';
import { esPartidaContableResidente } from '../utils/conceptosContables';
import './FinanzasUsuario.css';
import './FinanzasUsuarioMobile.css'; // Import explicit mobile overrides

// Helper: determina si una sanción ya fue cubierta por algún pago registrado o marcada como pagada
const isMultaCubiertaPorPago = (multa, pagosList) => {
    if (!multa) return false;
    if (multa.pagada === true || multa.estado === 'saldada') return true;
    if (!Array.isArray(pagosList) || pagosList.length === 0) return false;

    const descripcionMulta = String(multa.multaDescripcion || multa.descripcion || '')
        .toLowerCase()
        .trim();
    const montoMulta = Number(multa.monto || 0);

    return pagosList.some((pago) => {
        const conceptoLower = String(pago.concepto || '').toLowerCase();
        const esSancionAdmin =
            conceptoLower.includes('sancion') ||
            conceptoLower.includes('sansion') ||
            (conceptoLower.includes('multa') && conceptoLower.includes('administr'));

        if (!esSancionAdmin) return false;

        const descPago = String(
            pago.multaAdministrativa ||
            pago.multaAdminDesc ||
            pago.multaDescripcion ||
            ''
        )
            .toLowerCase()
            .trim();

        const montoPago = Number(pago.montoNeto || pago.monto || 0);

        if (descPago && descripcionMulta && descPago === descripcionMulta) {
            return Math.abs(montoPago - montoMulta) < 0.01;
        }

        // Si no hay descripción específica en el pago pero el concepto es de sanción y el monto coincide
        if (montoMulta > 0 && Math.abs(montoPago - montoMulta) < 0.01) {
            return true;
        }

        return false;
    });
};

// Helpers para Cuotas Extraordinarias
const getFrecuenciaText = (frecuencia) => {
    const mapa = {
        'monthly': 'Mensual',
        'bimonthly': 'Bimestral',
        'quarterly': 'Trimestral',
        'semiannual': 'Semestral',
        'yearly': 'Anual'
    };
    return mapa[frecuencia] || 'Mensual';
};

const getMesName = (numMes) => {
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    // La base de datos guarda meses como 1-12. Restamos 1 para el índice de array (0-11).
    const index = (Number(numMes) || 1) - 1;
    return meses[index % 12];
};

const calculaSiguienteCobro = (mesInicioStr, frecuenciaStr) => {
    const mesInicio = Number(mesInicioStr) || 1; // 1-12
    const date = new Date();
    let currentMonth = date.getMonth(); // 0-11
    let currentYear = date.getFullYear();

    let intervalo = 1;
    switch (frecuenciaStr) {
        case "monthly": intervalo = 1; break;
        case "bimonthly": intervalo = 2; break;
        case "quarterly": intervalo = 3; break;
        case "semiannual": intervalo = 6; break;
        case "yearly": intervalo = 12; break;
        default: intervalo = 1;
    }

    // Calcular el siguiente mes donde aplica
    for (let i = 0; i <= 12; i++) {
        const testMonth = (currentMonth + i) % 12; // 0-11
        // Comparamos el mes de prueba (0-11) con el inicio ajustado (mesInicio - 1)
        const diff = (testMonth - (mesInicio - 1) + 12) % 12;
        if (diff % intervalo === 0) {
            let testYear = currentYear + Math.floor((currentMonth + i) / 12);
            // Pasamos testMonth + 1 porque getMesName ahora espera 1-12
            return `${getMesName(testMonth + 1)} ${testYear}`;
        }
    }
    return 'Desconocido';
};

const aplicaEsteMes = (mesInicioStr, frecuenciaStr) => {
    const mesInicio = Number(mesInicioStr) || 1; // 1-12
    const currentMonth = new Date().getMonth(); // 0-11
    
    let intervalo = 1;
    switch (frecuenciaStr) {
        case "monthly": intervalo = 1; break;
        case "bimonthly": intervalo = 2; break;
        case "quarterly": intervalo = 3; break;
        case "semiannual": intervalo = 6; break;
        case "yearly": intervalo = 12; break;
        default: intervalo = 1;
    }

    // Ajustamos mesInicio a 0-indexado para la resta
    const diferenciaMeses = (currentMonth - (mesInicio - 1) + 12) % 12;
    return diferenciaMeses % intervalo === 0;
};


const FinanzasUsuario = () => {
    const { userData, casaActiva } = useAuth();
    const [pagos, setPagos] = useState([]);
    const [estadoCuenta, setEstadoCuenta] = useState(null);
    const [resolvedCasa, setResolvedCasa] = useState({ id: null, numero: null });
    const [configFinanciera, setConfigFinanciera] = useState(null);
    const [loadingPagos, setLoadingPagos] = useState(true);
    const [loadingEstado, setLoadingEstado] = useState(true);
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [mostrarTodosPagos, setMostrarTodosPagos] = useState(false);
    const [mostrarDetalleMultas, setMostrarDetalleMultas] = useState(false);
    const [mostrarDetalleCuotas, setMostrarDetalleCuotas] = useState(false);
    const [multasAdministrativas, setMultasAdministrativas] = useState([]);
    const [mesesPendientesData, setMesesPendientesData] = useState([]);
    const [loadingMultas, setLoadingMultas] = useState(true);
    const { debeMostrarAvisoFinanzas, userDebt } = useRestriccionMorosidad();
    const [avisoDismissed, setAvisoDismissed] = useState(false);
    const [mostrarModalDesglose, setMostrarModalDesglose] = useState(false);
    const [tabDesglose, setTabDesglose] = useState('mantenimiento'); // 'mantenimiento' | 'extraordinarias'
    const [aniosExpandidos, setAniosExpandidos] = useState({});
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [reciboSeleccionado, setReciboSeleccionado] = useState(null);
    const [recibosConfig, setRecibosConfig] = useState(null);
    const receiptRef = useRef(null);

    const fraccionamientoId = userData?.fraccionamientoId;
    const { modulos } = useModulos(fraccionamientoId);

    const [modalSubirComprobanteOpen, setModalSubirComprobanteOpen] = useState(false);
    const [archivoComprobante, setArchivoComprobante] = useState(null);
    const [previewComprobante, setPreviewComprobante] = useState(null);
    const [montoComprobante, setMontoComprobante] = useState('');
    const [notasComprobante, setNotasComprobante] = useState('');
    const [subiendoComprobante, setSubiendoComprobante] = useState(false);

    const handleArchivoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setArchivoComprobante(file);
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (ev) => setPreviewComprobante(ev.target.result);
                reader.readAsDataURL(file);
            } else {
                setPreviewComprobante(null);
            }
        }
    };

    const handleSubirComprobante = async (e) => {
        e.preventDefault();
        if (!archivoComprobante) {
            Swal.fire({
                icon: 'warning',
                title: 'Archivo requerido',
                text: 'Por favor selecciona la imagen o PDF de tu comprobante de pago.',
                confirmButtonColor: '#4f46e5'
            });
            return;
        }

        try {
            setSubiendoComprobante(true);
            const numeroCasa = estadoCuenta?.numeroCasa || userData?.casa || userData?.numeroCasa || 'S/N';
            const nombreResidente = estadoCuenta?.propietario || userData?.nombre || userData?.displayName || 'Residente';
            const fileExt = archivoComprobante.name.split('.').pop() || 'png';
            const filePath = `comprobantes_pago/${fraccionamientoId}/${numeroCasa}_${Date.now()}.${fileExt}`;
            const storageRef = ref(storage, filePath);
            
            await uploadBytesResumable(storageRef, archivoComprobante);
            const downloadUrl = await getDownloadURL(storageRef);

            await registrarComprobantePago({
                fraccionamientoId,
                casa: numeroCasa,
                residente: nombreResidente,
                archivoUrl: downloadUrl,
                monto: montoComprobante ? parseFloat(montoComprobante) : null,
                notas: notasComprobante.trim() || 'Comprobante subido desde Finanzas',
                usuarioUid: userData?.uid || null
            });

            setSubiendoComprobante(false);
            setModalSubirComprobanteOpen(false);
            setArchivoComprobante(null);
            setPreviewComprobante(null);
            setMontoComprobante('');
            setNotasComprobante('');

            Swal.fire({
                icon: 'success',
                title: '¡Comprobante enviado!',
                html: '<p>Tu comprobante de pago fue recibido exitosamente.</p><p style="font-size:0.9rem;color:#64748b;">La administración ha sido notificada para conciliar tu pago a la brevedad.</p>',
                confirmButtonColor: '#10b981'
            });
        } catch (err) {
            console.error('Error al subir comprobante:', err);
            setSubiendoComprobante(false);
            Swal.fire({
                icon: 'error',
                title: 'Error al enviar comprobante',
                text: 'Ocurrió un error al procesar tu comprobante. Por favor intenta nuevamente.',
                confirmButtonColor: '#ef4444'
            });
        }
    };

    const adeudosAgua = mesesPendientesData.filter(m => m.tipo === 'adeudo_agua' && (m.estado === 'pendiente' || m.estado === 'moroso'));
    const tieneAguaPendiente = adeudosAgua.length > 0;
    const montoAguaPendiente = adeudosAgua.reduce((sum, m) => sum + (Number(m.montoPendiente) || 0), 0);

    const numeroCasa = userData?.numeroCasa || userData?.casaNumero || userData?.numero;
    const casaId = userData?.casaId || userData?.casaID || userData?.idCasa || null;

    // Cargar configuración financiera del fraccionamiento
    useEffect(() => {
        let active = true;
        const loadConfig = async () => {
            if (!fraccionamientoId) {
                setLoadingConfig(false);
                return;
            }
            try {
                const fraccionamientoRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccionamientoRef);
                if (active && snap.exists()) {
                    const data = snap.data();
                    setRecibosConfig(data.configuracionRecibos || null);
                    const config = data.configuracionFinanciera || {};
                    setConfigFinanciera({
                        montoMensual: config.montoMensual || 0,
                        cuotaMensual: config.cuotaMensual || config.montoMensual || config.cuota_mensual || 0,
                        mesesPermitidos: Number(config.interesesMesesPermitidosDeuda ?? config.mesesPermitidos ?? config.meses_permitidos ?? 1),
                        corteDia: config.corteDia || 1,
                        corteDiaResidentes: config.corteDiaResidentes || config.corteDia || 1,
                        sancionesInicioDia: config.sancionesInicioDia || config.corteDia || 1,
                        interesesEnabled: Boolean(config.interesesEnabled),
                        interesesType: config.interesesType || config.tipoInteres || 'fixed',
                        interesesValue: config.interesesValue !== undefined && config.interesesValue !== '' ? Number(config.interesesValue) : 0,
                        discountEnabled: Boolean(config.discountEnabled),
                        discountPercent: config.discountPercent || 0,
                        multasEnabled: Boolean(config.multasEnabled),
                        multasList: Array.isArray(config.multasAdministrativasList) ? config.multasAdministrativasList : [],
                        cuotaExtras: Array.isArray(config.cuotaExtras) ? config.cuotaExtras : [],
                        nombreFraccionamiento: data.nombre || 'Fraccionamiento'
                    });
                }
            } catch (error) {
                console.error('Error cargando configuración financiera:', error);
            } finally {
                if (active) setLoadingConfig(false);
            }
        };
        loadConfig();
        return () => {
            active = false;
        };
    }, [fraccionamientoId]);

    // Cargar estado de cuenta del residente
    useEffect(() => {
        let active = true;
        const casaId = casaActiva?.id || userData?.casaId || userData?.idCasa;
        const numeroCasa = casaActiva?.numero || casaActiva?.numeroCasa || userData?.numeroCasa || userData?.numero_casa;

        const loadEstado = async () => {
            if (!fraccionamientoId) {
                console.warn('⚠️ FinanzasUsuario - Falta fraccionamientoId');
                setLoadingEstado(false);
                return;
            }

            // Esperar a que configFinanciera esté disponible
            if (!configFinanciera || configFinanciera.mesesPermitidos === undefined) {
                console.log('⏳ FinanzasUsuario - Esperando configuración financiera...');
                return;
            }

            console.log('🔍 FinanzasUsuario - Buscando casa:', {
                fraccionamientoId,
                numeroCasa,
                casaId,
                userId: userData?.uid,
                email: userData?.email,
                userDataKeys: userData ? Object.keys(userData) : [],
                tieneConfig: !!configFinanciera
            });

            try {
                // Si tenemos casaId, usamos ese como docId
                // Si tenemos numeroCasa, hacemos una query para encontrar la casa con ese numero
                if (casaId) {
                    // Buscar por casaId directo
                    const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                    const casaDocRef = doc(casasRef, casaId);
                    const snap = await getDoc(casaDocRef);

                    if (active && snap.exists()) {
                        const data = snap.data();
                        // CORREGIDO: Usar calcularAdeudoVisibleDesdeObjeto para respetar período de gracia
                        // Durante meses y días de gracia: muestra solo adeudoCapital
                        // Después de meses y días de gracia: muestra adeudo total
                        const adeudoVisible = calcularAdeudoVisibleDesdeObjeto(data, configFinanciera);
                        const adeudoReal = Number(data.adeudo ?? data.deuda ?? data.montoAdeudo ?? 0);
                        console.log('✅ FinanzasUsuario - Casa encontrada por ID:', {
                            ...data,
                            adeudoReal: adeudoReal,
                            adeudoVisible: adeudoVisible,
                            adeudoCampo: data.adeudo
                        });
                        setResolvedCasa({ id: casaId, numero: data.numero || casaId, ...data });
                        setEstadoCuenta({
                            adeudo: adeudoVisible, // Mostrar adeudo visible (respeta período de gracia)
                            adeudoReal: adeudoReal,
                            adeudoCapital: Number(data.adeudoCapital || 0),
                            adeudoIntereses: Number(data.adeudoIntereses || 0),
                            saldoFavor: data.saldoFavor || 0,
                            mesesAdeudo: data.mesesAdeudo || 0, // Leer directamente de Firebase
                            moroso: (data.saldoFavor || 0) > 0 ? false : calcularEstadoMoroso(adeudoVisible, Number(configFinanciera?.cuotaMensual || 0), Number(configFinanciera?.mesesPermitidos || 1)),
                            numeroCasa: data.numero || data.numeroCasa || casaId || 'N/A',
                            propietario: data.nombre_residente || data.propietario || data.nombre || 'N/A'
                        });

                        // MEJORADO: Auto-actualizar userData si falta casaId o numeroCasa
                        if (userData?.uid && (!userData.casaId || !userData.numeroCasa)) {
                            try {
                                const userDocRef = doc(db, 'usuarios', userData.uid);
                                const updateData = {};
                                if (!userData.casaId) updateData.casaId = casaId;
                                if (!userData.numeroCasa) updateData.numeroCasa = data.numero || casaId;
                                await updateDoc(userDocRef, updateData);
                                console.log('✅ FinanzasUsuario - userData actualizado automáticamente:', updateData);
                            } catch (updateErr) {
                                console.warn('⚠️ FinanzasUsuario - Error actualizando userData:', updateErr);
                            }
                        }

                        setLoadingEstado(false);
                    } else if (active) {
                        console.warn('❌ Casa con ID no encontrada, intentando por numero...');
                        // Si no encuentra por ID, intenta por numero
                        searchCasaByNumero(numeroCasa);
                    }
                } else if (numeroCasa) {
                    // Buscar por numero usando query
                    searchCasaByNumero(numeroCasa);
                } else {
                    // Fallback: intentar resolver casa por userId o email con múltiples estrategias
                    try {
                        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                        let foundData = null;
                        let foundId = null;

                        // Estrategia 1: Buscar por userId
                        if (userData?.uid) {
                            console.log('🔍 [Fallback] Buscando casa por userId:', userData.uid);
                            const qByUser = query(casasRef, where('userId', '==', userData.uid));
                            const snapByUser = await getDocs(qByUser);
                            if (!snapByUser.empty) {
                                foundData = snapByUser.docs[0].data();
                                foundId = snapByUser.docs[0].id;
                                console.log('✅ [Fallback] Casa resuelta por userId:', { foundId, numero: foundData.numero });
                            } else {
                                console.log('⚠️ [Fallback] No se encontró casa por userId');
                            }
                        }

                        // Estrategia 2: Buscar por email (normalizado)
                        if (!foundData && userData?.email) {
                            const emailNormalizado = String(userData.email).toLowerCase().trim();
                            console.log('🔍 [Fallback] Buscando casa por email normalizado:', emailNormalizado);
                            const qByEmail = query(casasRef, where('email', '==', emailNormalizado));
                            const snapByEmail = await getDocs(qByEmail);
                            if (!snapByEmail.empty) {
                                foundData = snapByEmail.docs[0].data();
                                foundId = snapByEmail.docs[0].id;
                                console.log('✅ [Fallback] Casa resuelta por email normalizado:', { foundId, numero: foundData.numero });
                            } else {
                                console.log('⚠️ [Fallback] No se encontró casa por email normalizado, intentando sin normalizar...');
                                // Intentar sin normalizar (por si acaso)
                                const qByEmailRaw = query(casasRef, where('email', '==', userData.email));
                                const snapByEmailRaw = await getDocs(qByEmailRaw);
                                if (!snapByEmailRaw.empty) {
                                    foundData = snapByEmailRaw.docs[0].data();
                                    foundId = snapByEmailRaw.docs[0].id;
                                    console.log('✅ [Fallback] Casa resuelta por email sin normalizar:', { foundId, numero: foundData.numero });
                                }
                            }
                        }

                        // Estrategia 3: Obtener todas las casas y buscar por email o userId manualmente (último recurso)
                        if (!foundData && (userData?.email || userData?.uid)) {
                            console.log('🔍 [Fallback] Último recurso: obteniendo todas las casas para búsqueda manual...');
                            try {
                                const allCasasSnap = await getDocs(casasRef);
                                const emailNormalizado = userData.email ? String(userData.email).toLowerCase().trim() : null;
                                const emailVariants = userData.email ? [
                                    emailNormalizado,
                                    userData.email,
                                    userData.email?.toLowerCase(),
                                    userData.email?.trim(),
                                    userData.email?.toLowerCase()?.trim()
                                ].filter(Boolean) : [];

                                console.log('🔍 [Fallback] Búsqueda manual - Parámetros:', {
                                    totalCasas: allCasasSnap.size,
                                    emailUser: emailNormalizado,
                                    emailVariants: emailVariants,
                                    userId: userData.uid
                                });

                                for (const casaDoc of allCasasSnap.docs) {
                                    const casaData = casaDoc.data();
                                    const casaEmail = casaData.email ? String(casaData.email).toLowerCase().trim() : null;
                                    const casaUserId = casaData.userId;

                                    // Log detallado de cada casa para debugging
                                    console.log('🔍 [Fallback] Revisando casa:', {
                                        casaId: casaDoc.id,
                                        numero: casaData.numero,
                                        emailCasa: casaEmail,
                                        userIdCasa: casaUserId,
                                        emailMatch: casaEmail && emailVariants.includes(casaEmail),
                                        userIdMatch: casaUserId === userData.uid
                                    });

                                    // Comparar email (más flexible - tolera typos menores)
                                    let emailMatch = false;
                                    if (casaEmail && emailNormalizado) {
                                        // Comparación exacta
                                        if (casaEmail === emailNormalizado) {
                                            emailMatch = true;
                                        } else {
                                            // Comparación flexible: verificar si son muy similares (typos menores)
                                            // Comparar sin puntos y con variantes comunes
                                            const normalizeForComparison = (email) => {
                                                return email
                                                    .replace(/\./g, '') // Quitar puntos
                                                    .toLowerCase()
                                                    .trim();
                                            };

                                            const casaEmailNormalized = normalizeForComparison(casaEmail);
                                            const userEmailNormalized = normalizeForComparison(emailNormalizado);

                                            // Si después de normalizar son iguales, o si la diferencia es mínima (1-2 caracteres)
                                            if (casaEmailNormalized === userEmailNormalized) {
                                                emailMatch = true;
                                            } else {
                                                // Calcular similitud mejorada (diferencia de longitud y caracteres comunes)
                                                const lengthDiff = Math.abs(casaEmailNormalized.length - userEmailNormalized.length);
                                                const minLength = Math.min(casaEmailNormalized.length, userEmailNormalized.length);

                                                // Si tienen la misma longitud o diferencia de 1-2 caracteres
                                                if (lengthDiff <= 2 && minLength > 5) {
                                                    // Calcular similitud por posición
                                                    let commonChars = 0;
                                                    const maxLength = Math.max(casaEmailNormalized.length, userEmailNormalized.length);
                                                    for (let i = 0; i < maxLength; i++) {
                                                        if (casaEmailNormalized[i] === userEmailNormalized[i]) {
                                                            commonChars++;
                                                        }
                                                    }

                                                    // También calcular similitud por caracteres únicos (para detectar typos)
                                                    const casaChars = new Set(casaEmailNormalized.split(''));
                                                    const userChars = new Set(userEmailNormalized.split(''));
                                                    const commonUniqueChars = [...casaChars].filter(c => userChars.has(c)).length;
                                                    const totalUniqueChars = Math.max(casaChars.size, userChars.size);
                                                    const uniqueSimilarity = totalUniqueChars > 0 ? commonUniqueChars / totalUniqueChars : 0;

                                                    const positionSimilarity = commonChars / maxLength;
                                                    const overallSimilarity = (positionSimilarity * 0.7) + (uniqueSimilarity * 0.3); // Ponderación

                                                    // Si la similitud es >= 90% o si tienen la misma longitud y >= 85% de similitud
                                                    if (overallSimilarity >= 0.90 || (lengthDiff === 0 && overallSimilarity >= 0.85)) {
                                                        emailMatch = true;
                                                        console.log('🔍 [Fallback] Email match por similitud:', {
                                                            casaEmail: casaEmail,
                                                            userEmail: emailNormalizado,
                                                            positionSimilarity: (positionSimilarity * 100).toFixed(1) + '%',
                                                            uniqueSimilarity: (uniqueSimilarity * 100).toFixed(1) + '%',
                                                            overallSimilarity: (overallSimilarity * 100).toFixed(1) + '%',
                                                            lengthDiff: lengthDiff
                                                        });
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    if (emailMatch) {
                                        foundData = casaData;
                                        foundId = casaDoc.id;
                                        console.log('✅ [Fallback] Casa encontrada por email (búsqueda manual):', {
                                            foundId,
                                            numero: foundData.numero,
                                            nombre_residente: foundData.nombre_residente,
                                            emailCasa: casaEmail,
                                            emailCasaOriginal: casaData.email,
                                            emailUser: emailNormalizado,
                                            emailUserOriginal: userData.email
                                        });
                                        break;
                                    }

                                    // Comparar userId
                                    if (casaUserId === userData.uid) {
                                        foundData = casaData;
                                        foundId = casaDoc.id;
                                        console.log('✅ [Fallback] Casa encontrada por userId (búsqueda manual):', {
                                            foundId,
                                            numero: foundData.numero,
                                            nombre_residente: foundData.nombre_residente,
                                            userId: casaUserId
                                        });
                                        break;
                                    }
                                }

                                if (!foundData) {
                                    console.warn('❌ [Fallback] No se encontró casa después de búsqueda manual. Total casas revisadas:', allCasasSnap.size);
                                    // Log de todas las casas para debugging
                                    console.log('📋 [Fallback] Resumen de casas disponibles:',
                                        allCasasSnap.docs.map(doc => ({
                                            id: doc.id,
                                            numero: doc.data().numero,
                                            email: doc.data().email,
                                            userId: doc.data().userId,
                                            nombre_residente: doc.data().nombre_residente
                                        }))
                                    );
                                }
                            } catch (manualErr) {
                                console.error('❌ [Fallback] Error en búsqueda manual:', manualErr);
                            }
                        }

                        if (active && foundData) {
                            // CORREGIDO: Usar calcularAdeudoVisibleDesdeObjeto para respetar período de gracia
                            // Durante meses y días de gracia: muestra solo adeudoCapital
                            // Después de meses y días de gracia: muestra adeudo total
                            const adeudoVisible = calcularAdeudoVisibleDesdeObjeto(foundData, configFinanciera);
                            const adeudoReal = Number(foundData.adeudo ?? foundData.deuda ?? foundData.montoAdeudo ?? 0);
                            setResolvedCasa({ id: foundId, numero: foundData.numero || foundId, ...foundData });
                            setEstadoCuenta({
                                adeudo: adeudoVisible, // Mostrar adeudo visible (respeta período de gracia)
                                adeudoReal: adeudoReal,
                                adeudoCapital: Number(foundData.adeudoCapital || 0),
                                adeudoIntereses: Number(foundData.adeudoIntereses || 0),
                                saldoFavor: foundData.saldoFavor || 0,
                                mesesAdeudo: foundData.mesesAdeudo || 0, // Leer directamente de Firebase
                                moroso: (foundData.saldoFavor || 0) > 0 ? false : calcularEstadoMoroso(adeudoVisible, Number(configFinanciera?.cuotaMensual || 0), Number(configFinanciera?.mesesPermitidos || 1)),
                                numeroCasa: foundData.numero || foundId,
                                propietario: foundData.nombre_residente || foundData.propietario || 'N/A'
                            });

                            // MEJORADO: Auto-actualizar userData si falta casaId o numeroCasa (sincronización bidireccional)
                            if (userData?.uid) {
                                try {
                                    const userDocRef = doc(db, 'usuarios', userData.uid);
                                    const userUpdateData = {};
                                    if (!userData.casaId || userData.casaId !== foundId) {
                                        userUpdateData.casaId = foundId;
                                    }
                                    if (foundData.numero && (!userData.numeroCasa || userData.numeroCasa !== foundData.numero)) {
                                        userUpdateData.numeroCasa = foundData.numero;
                                    }
                                    if (Object.keys(userUpdateData).length > 0) {
                                        await updateDoc(userDocRef, userUpdateData);
                                        console.log('✅ [Fallback] userData actualizado automáticamente (sincronización bidireccional):', userUpdateData);
                                    }
                                } catch (updateErr) {
                                    console.warn('⚠️ [Fallback] Error actualizando userData:', updateErr);
                                }
                            }

                            // MEJORADO: Auto-actualizar casa.userId y casa.email SIEMPRE (sincronización bidireccional)
                            // Esto asegura que la casa tenga los datos correctos del usuario, incluso si había un typo
                            if (userData?.uid) {
                                try {
                                    const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', foundId);
                                    const casaUpdateData = {};

                                    // SIEMPRE actualizar userId si es diferente (o si no existe)
                                    const emailCasaNormalizado = foundData.email ? String(foundData.email).toLowerCase().trim() : null;
                                    const emailUserNormalizado = userData.email ? String(userData.email).toLowerCase().trim() : null;

                                    if (!foundData.userId || foundData.userId !== userData.uid) {
                                        casaUpdateData.userId = userData.uid;
                                        console.log('🔄 [Fallback] Actualizando casa.userId:', {
                                            anterior: foundData.userId || 'null',
                                            nuevo: userData.uid
                                        });
                                    }

                                    // SIEMPRE actualizar email si es diferente (corrige typos)
                                    if (userData.email && (!emailCasaNormalizado || emailCasaNormalizado !== emailUserNormalizado)) {
                                        casaUpdateData.email = userData.email; // Usar el email original del usuario (no normalizado)
                                        console.log('🔄 [Fallback] Actualizando casa.email (corrigiendo typo):', {
                                            anterior: foundData.email || 'null',
                                            nuevo: userData.email,
                                            normalizadoAnterior: emailCasaNormalizado,
                                            normalizadoNuevo: emailUserNormalizado
                                        });
                                    }

                                    if (Object.keys(casaUpdateData).length > 0) {
                                        await updateDoc(casaDocRef, casaUpdateData);
                                        console.log('✅ [Fallback] Casa actualizada automáticamente (sincronización bidireccional):', casaUpdateData);

                                        // Actualizar foundData localmente para reflejar los cambios
                                        if (casaUpdateData.userId) foundData.userId = casaUpdateData.userId;
                                        if (casaUpdateData.email) foundData.email = casaUpdateData.email;
                                    } else {
                                        console.log('ℹ️ [Fallback] Casa ya está sincronizada, no se requieren actualizaciones');
                                    }
                                } catch (updateErr) {
                                    console.error('❌ [Fallback] Error actualizando casa:', updateErr);
                                }
                            }
                        } else if (active) {
                            console.error('❌ [Fallback] No se pudo resolver la casa para el usuario después de todas las estrategias.', {
                                fraccionamientoId,
                                userId: userData?.uid,
                                email: userData?.email,
                                userDataKeys: userData ? Object.keys(userData) : []
                            });
                            // Aún así, intentar establecer un estado vacío para evitar loading infinito
                            setEstadoCuenta({
                                adeudo: 0,
                                adeudoReal: 0,
                                adeudoCapital: 0,
                                adeudoIntereses: 0,
                                saldoFavor: 0,
                                mesesAdeudo: 0,
                                moroso: false,
                                numeroCasa: null,
                                propietario: 'N/A'
                            });
                        }
                        if (active) setLoadingEstado(false);
                    } catch (fallbackErr) {
                        console.error('❌ Error resolviendo casa por fallback:', fallbackErr);
                        if (active) setLoadingEstado(false);
                    }
                }
            } catch (error) {
                console.error('❌ Error cargando estado de cuenta:', error);
                setLoadingEstado(false);
            }

            async function searchCasaByNumero(numero) {
                try {
                    const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                    // Query para buscar casa por campo "numero"
                    const q = query(casasRef, where('numero', '==', String(numero)));
                    const snapshot = await getDocs(q);

                    if (active) {
                        if (!snapshot.empty) {
                            const data = snapshot.docs[0].data();
                            const docId = snapshot.docs[0].id;
                            // CORREGIDO: Usar calcularAdeudoVisibleDesdeObjeto para respetar período de gracia
                            // Durante meses y días de gracia: muestra solo adeudoCapital
                            // Después de meses y días de gracia: muestra adeudo total
                            const adeudoVisible = calcularAdeudoVisibleDesdeObjeto(data, configFinanciera);
                            const adeudoReal = Number(data.adeudo ?? data.deuda ?? data.montoAdeudo ?? 0);
                            console.log('✅ FinanzasUsuario - Casa encontrada por numero:', {
                                ...data,
                                adeudoReal: adeudoReal,
                                adeudoVisible: adeudoVisible,
                                adeudoCampo: data.adeudo
                            });
                            setResolvedCasa({ id: docId, numero: data.numero || numero, ...data });
                            setEstadoCuenta({
                                adeudo: adeudoVisible, // Mostrar adeudo visible (respeta período de gracia)
                                adeudoReal: adeudoReal,
                                adeudoCapital: Number(data.adeudoCapital || 0),
                                adeudoIntereses: Number(data.adeudoIntereses ?? data.adeudoInteres ?? 0),
                                saldoFavor: data.saldoFavor || 0,
                                mesesAdeudo: data.mesesAdeudo || 0, // Leer directamente de Firebase
                                moroso: (data.saldoFavor || 0) > 0 ? false : calcularEstadoMoroso(adeudoVisible, Number(configFinanciera?.cuotaMensual || 0), Number(configFinanciera?.mesesPermitidos || 1)),
                                numeroCasa: data.numero || numero,
                                propietario: data.nombre_residente || data.propietario || 'N/A'
                            });

                            // MEJORADO: Auto-actualizar userData si falta casaId o numeroCasa
                            if (userData?.uid && (!userData.casaId || !userData.numeroCasa)) {
                                try {
                                    const userDocRef = doc(db, 'usuarios', userData.uid);
                                    const updateData = {};
                                    if (!userData.casaId) updateData.casaId = docId;
                                    if (!userData.numeroCasa) updateData.numeroCasa = data.numero || numero;
                                    await updateDoc(userDocRef, updateData);
                                    console.log('✅ FinanzasUsuario - userData actualizado automáticamente (por numero):', updateData);
                                } catch (updateErr) {
                                    console.warn('⚠️ FinanzasUsuario - Error actualizando userData:', updateErr);
                                }
                            }

                            // MEJORADO: Auto-actualizar casa.userId si falta
                            if (userData?.uid && data.userId !== userData.uid) {
                                try {
                                    const casaDocRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', docId);
                                    await updateDoc(casaDocRef, { userId: userData.uid });
                                    console.log('✅ FinanzasUsuario - casa.userId actualizado automáticamente (por numero):', userData.uid);
                                } catch (updateErr) {
                                    console.warn('⚠️ FinanzasUsuario - Error actualizando casa.userId:', updateErr);
                                }
                            }
                        } else {
                            console.error('❌ FinanzasUsuario - Casa no encontrada. Numero buscado:', numero);
                        }
                        setLoadingEstado(false);
                    }
                } catch (error) {
                    console.error('❌ Error en búsqueda por numero:', error);
                    if (active) setLoadingEstado(false);
                }
            }
        };

        // Solo cargar estado si configFinanciera ya está disponible
        if (configFinanciera && configFinanciera.mesesPermitidos !== undefined) {
            loadEstado();
        }
        return () => {
            active = false;
        };
    }, [fraccionamientoId, numeroCasa, casaId, userData, configFinanciera, casaActiva?.id]);

    // Recalcular adeudo visible cuando cambie configFinanciera o estadoCuenta
    useEffect(() => {
        if (!estadoCuenta || !configFinanciera || !configFinanciera.mesesPermitidos) {
            return;
        }

        // Si ya tenemos los datos de la casa, recalcular el adeudo visible
        if (resolvedCasa?.id) {
            const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
            const casaDocRef = doc(casasRef, resolvedCasa.id);
            getDoc(casaDocRef).then(snap => {
                if (snap.exists()) {
                    const data = snap.data();
                    const adeudoVisible = calcularAdeudoVisibleDesdeObjeto(data, configFinanciera);
                    const adeudoReal = Number(data.adeudo ?? data.deuda ?? data.montoAdeudo ?? 0);

                    setEstadoCuenta(prev => ({
                        ...prev,
                        adeudo: adeudoVisible,
                        adeudoReal: adeudoReal,
                        mesesAdeudo: data.mesesAdeudo || 0,
                        saldoFavor: data.saldoFavor || 0
                    }));

                    console.log('🔄 FinanzasUsuario - Adeudo visible recalculado:', {
                        adeudoVisible,
                        adeudoReal,
                        mesesAdeudo: data.mesesAdeudo,
                        mesesPermitidos: configFinanciera.mesesPermitidos
                    });
                }
            }).catch(err => {
                console.error('Error recalculando adeudo visible:', err);
            });
        }
    }, [configFinanciera, resolvedCasa, fraccionamientoId]);

    // Cargar mesesPendientes (para cuotas extraordinarias)
    useEffect(() => {
        if (!fraccionamientoId || !resolvedCasa?.id) return;
        
        const mesesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas', resolvedCasa.id, 'mesesPendientes');
        const unsubscribe = onSnapshot(mesesRef, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMesesPendientesData(data);
        }, (error) => {
            console.error('Error cargando mesesPendientes:', error);
        });
        
        return () => unsubscribe();
    }, [fraccionamientoId, resolvedCasa?.id]);


    // Cargar pagos realizados (ingresos) por vivienda
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoadingPagos(false);
            return;
        }

        // Resolver identificadores efectivos
        const effectiveCasaId = casaId || resolvedCasa.id || null;
        const effectiveNumeroCasa = numeroCasa || resolvedCasa.numero || null;

        console.log('📊 [Pagos] Intentando cargar pagos con:', {
            fraccionamientoId,
            effectiveCasaId,
            effectiveNumeroCasa,
            casaId,
            numeroCasa,
            resolvedCasa,
        });

        if (!effectiveCasaId && !effectiveNumeroCasa) {
            // No podemos suscribir pagos sin una casa; finalizar loading
            console.warn('⚠️ [Pagos] No se pudo resolver casa para cargar pagos');
            setLoadingPagos(false);
            return;
        }

        const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');

        // Intentar primero con casaId, luego con casaNumero
        let q;
        if (effectiveCasaId) {
            console.log('📊 [Pagos] Consultando por casaId:', effectiveCasaId);
            q = query(ingresosRef, where('casaId', '==', effectiveCasaId), orderBy('fecha', 'desc'));
        } else {
            console.log('📊 [Pagos] Consultando por casaNumero:', effectiveNumeroCasa);
            q = query(ingresosRef, where('casaNumero', '==', String(effectiveNumeroCasa)), orderBy('fecha', 'desc'));
        }

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const todosLosIngresos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar exclusivamente pagos registrados por la administración (excluir banderas, eliminados y cargos automáticos)
            const pagosReales = todosLosIngresos.filter(pago => {
                // Excluir recibos eliminados o cancelados por administración
                if (pago.deleted === true || pago.eliminado === true || pago.activo === false) return false;

                const metodo = String(pago.metodoPago || '').trim().toLowerCase();
                const concepto = String(pago.concepto || '').toLowerCase();
                const tipo = String(pago.tipo || '').toLowerCase();

                // Excluir registros automáticos del sistema
                if (metodo === 'registro_automatico') return false;
                if (tipo === 'registro_mensual' || tipo === 'cargo_mensual') return false;
                if (pago.esRegistroMensual === true) return false;
                if (concepto.includes('saldo final de mes')) return false;
                if (concepto.startsWith('adeudo ') || concepto.startsWith('sin adeudo ')) return false;

                // Si no tiene método o es N/A y el concepto es de adeudo/recargo
                const esIndicadorVisual = !metodo || metodo === 'n/a' || metodo === '';
                if (esIndicadorVisual) {
                    if (concepto.includes('adeudo historico') ||
                        concepto.includes('adeudo histórico') ||
                        concepto.includes('recargo por morosidad')) {
                        return false;
                    }
                }

                return true;
            });

            console.log('✅ [Pagos] Pagos reales cargados:', pagosReales.length, 'de un total de', todosLosIngresos.length);
            setPagos(pagosReales);
            setLoadingPagos(false);
        }, (error) => {
            console.error('❌ [Pagos] Error cargando pagos:', error);

            if (error.code === 'failed-precondition' || error.message.includes('index')) {
                console.warn('⚠️ El índice de Firestore aún se está construyendo. Esto puede tardar unos minutos.');
                console.warn('📝 Para crear el índice manualmente, usa este enlace:', error.message);
            }

            setLoadingPagos(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId, casaId, numeroCasa, resolvedCasa.id, resolvedCasa.numero]);

    // Cargar multas administrativas
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoadingMultas(false);
            return;
        }

        const effectiveCasaId = casaId || resolvedCasa.id || null;
        const effectiveNumeroCasa = numeroCasa || resolvedCasa.numero || null;

        console.log('🔍 [Multas] Buscando multas con:', {
            fraccionamientoId,
            casaId: effectiveCasaId,
            numeroCasa: effectiveNumeroCasa,
            resolvedCasaId: resolvedCasa.id,
            resolvedCasaNumero: resolvedCasa.numero
        });

        if (!effectiveCasaId && !effectiveNumeroCasa) {
            console.warn('⚠️ [Multas] No hay casaId ni numeroCasa disponible aún');
            setLoadingMultas(false);
            return;
        }

        const loadMultas = async () => {
            try {
                const multasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
                let multasEncontradas = [];

                // Función helper para ordenar multas por fecha de captura (más reciente primero)
                const ordenarMultas = (multas) => {
                    return multas.sort((a, b) => {
                        const fechaA = a.fechaCaptura?.toDate ? a.fechaCaptura.toDate() : (a.fechaCaptura ? new Date(a.fechaCaptura) : new Date(0));
                        const fechaB = b.fechaCaptura?.toDate ? b.fechaCaptura.toDate() : (b.fechaCaptura ? new Date(b.fechaCaptura) : new Date(0));
                        return fechaB.getTime() - fechaA.getTime(); // Orden descendente
                    });
                };

                // Intentar buscar por casaId primero (sin orderBy para evitar necesidad de índice)
                if (effectiveCasaId) {
                    try {
                        const qById = query(multasRef, where('casaId', '==', effectiveCasaId));
                        const snapshotById = await getDocs(qById);
                        multasEncontradas = snapshotById.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        // Filtrar multas pagadas y multas cubiertas por un pago
                        multasEncontradas = multasEncontradas.filter(
                            (multa) => !multa.pagada && !isMultaCubiertaPorPago(multa, pagos)
                        );
                        multasEncontradas = ordenarMultas(multasEncontradas);
                        console.log(`✅ [Multas] Encontradas ${multasEncontradas.length} multas no pagadas por casaId (después de cruzar con pagos):`, effectiveCasaId);
                    } catch (errorById) {
                        console.warn('⚠️ [Multas] Error buscando por casaId, intentando por numero:', errorById);
                    }
                }

                // Si no se encontraron por casaId, intentar por numeroCasa
                if (multasEncontradas.length === 0 && effectiveNumeroCasa) {
                    try {
                        // Intentar con el formato exacto
                        const numeroFormateado = String(effectiveNumeroCasa).trim();
                        const qByNumero = query(multasRef, where('casaNumero', '==', numeroFormateado));
                        const snapshotByNumero = await getDocs(qByNumero);
                        multasEncontradas = snapshotByNumero.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        // Filtrar multas pagadas y multas cubiertas por un pago
                        multasEncontradas = multasEncontradas.filter(
                            (multa) => !multa.pagada && !isMultaCubiertaPorPago(multa, pagos)
                        );
                        multasEncontradas = ordenarMultas(multasEncontradas);
                        console.log(`✅ [Multas] Encontradas ${multasEncontradas.length} multas no pagadas por casaNumero (después de cruzar con pagos):`, numeroFormateado);
                    } catch (errorByNumero) {
                        console.warn('⚠️ [Multas] Error buscando por casaNumero:', errorByNumero);
                    }
                }

                // Configurar listener en tiempo real (sin orderBy para evitar necesidad de índice)
                let unsubscribe = null;
                if (effectiveCasaId) {
                    const q = query(multasRef, where('casaId', '==', effectiveCasaId));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        const multas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        // Filtrar multas pagadas y multas cubiertas por un pago
                        const multasVigentes = multas.filter(
                            (multa) => !multa.pagada && !isMultaCubiertaPorPago(multa, pagos)
                        );
                        const multasOrdenadas = ordenarMultas(multasVigentes);
                        console.log(`📊 [Multas] Actualización en tiempo real: ${multasOrdenadas.length} multas vigentes (de ${multas.length} totales)`);
                        setMultasAdministrativas(multasOrdenadas);
                        setLoadingMultas(false);
                    }, (error) => {
                        console.error('❌ [Multas] Error en listener:', error);
                        setLoadingMultas(false);
                    });
                } else if (effectiveNumeroCasa) {
                    const numeroFormateado = String(effectiveNumeroCasa).trim();
                    const q = query(multasRef, where('casaNumero', '==', numeroFormateado));
                    unsubscribe = onSnapshot(q, (snapshot) => {
                        const multas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        const multasVigentes = multas.filter(
                            (multa) => !multa.pagada && !isMultaCubiertaPorPago(multa, pagos)
                        );
                        const multasOrdenadas = ordenarMultas(multasVigentes);
                        console.log(`📊 [Multas] Actualización en tiempo real: ${multasOrdenadas.length} multas vigentes (de ${multas.length} totales)`);
                        setMultasAdministrativas(multasOrdenadas);
                        setLoadingMultas(false);
                    }, (error) => {
                        console.error('❌ [Multas] Error en listener:', error);
                        setLoadingMultas(false);
                    });
                }

                // Establecer las multas encontradas inicialmente (ya filtradas)
                if (multasEncontradas.length > 0) {
                    setMultasAdministrativas(multasEncontradas);
                }
                setLoadingMultas(false);

                return () => {
                    if (unsubscribe) unsubscribe();
                };
            } catch (error) {
                console.error('❌ [Multas] Error en loadMultas:', error);
                setLoadingMultas(false);
            }
        };

        loadMultas();
    }, [fraccionamientoId, casaId, numeroCasa, resolvedCasa.id, resolvedCasa.numero, pagos]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const formatDateTime = (timestamp) => {
        // Normaliza distintos formatos (Timestamp de Firestore, epoch en segundos/ms, ISO string)
        if (!timestamp) return 'Fecha no disponible';

        const normalizeDate = (value) => {
            if (!value) return null;
            if (value.toDate) return value.toDate(); // Firestore Timestamp
            if (typeof value === 'number') {
                // Si viene en segundos (<= 1e12) multiplícalo, si ya está en ms úsalo directo
                return new Date(value > 1e12 ? value : value * 1000);
            }
            if (typeof value === 'string') {
                const parsed = new Date(value);
                if (!isNaN(parsed.getTime())) return parsed;
            }
            return null;
        };

        try {
            const date = normalizeDate(timestamp);
            if (!date) return 'Fecha no disponible';

            // Formato legible y consistente en zona horaria de MX
            return date.toLocaleString('es-MX', {
                timeZone: 'America/Mexico_City',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formateando fecha:', error);
            return 'Fecha no disponible';
        }
    };

    const formatFechaPago = (pago) => {
        const raw = pago?.fechaPagoOriginal || pago?.fechaPago || (Array.isArray(pago?.pagos) && pago.pagos[0]?.fechaPago) || null;
        if (!raw) return null;

        if (typeof raw === 'string') {
            const clean = raw.split('T')[0];
            const parts = clean.split('-');
            if (parts.length === 3) {
                const [y, m, d] = parts;
                return `${d}/${m}/${y}`;
            }
            if (clean.includes('/')) {
                return clean;
            }
        }

        try {
            const d = raw.toDate ? raw.toDate() : new Date(raw);
            if (!isNaN(d.getTime())) {
                const dia = String(d.getDate()).padStart(2, '0');
                const mes = String(d.getMonth() + 1).padStart(2, '0');
                const anio = d.getFullYear();
                return `${dia}/${mes}/${anio}`;
            }
        } catch (e) {
            console.warn('Error formateando fecha de pago:', e);
        }
        return null;
    };

    const getInterestStatusLabel = (enabled) => {
        return enabled ? 'Activo' : 'Desactivado (OFF)';
    };

    // Calcular sumatorias para las tarjetas del estado de cuenta
    const totalMultasPendientes = multasAdministrativas.reduce((sum, m) => sum + Number(m.monto || 0), 0);
    const totalCuotasExtrasPendientes = mesesPendientesData
        .filter(m => m.estado !== 'pagado_completo' && m.estado !== 'pagado_totalmente' && (m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria'))
        .reduce((sum, m) => sum + Number(m.montoPendiente ?? m.monto ?? 0), 0);

    const cuotasExtrasPendientesList = mesesPendientesData
        .filter(m => m.estado !== 'pagado_completo' && m.estado !== 'pagado_totalmente' && (m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria'));
    const nombresCuotasExtrasPendientes = cuotasExtrasPendientesList
        .map(m => m.concepto || m.descripcion || 'Cuota Extra')
        .filter((value, index, self) => self.indexOf(value) === index)
        .join(', ');

    // Meses de mantenimiento ordinario pendientes
    const mesesMantenimientoPendientesList = useMemo(() => {
        return mesesPendientesData
            .filter(m => {
                const isExtra = m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria' || m.esExtraordinaria;
                const isAgua = m.id?.startsWith('AGUA-') || m.tipo === 'adeudo_agua' || m.tipo === 'consumo_agua';
                const est = String(m.estado || '').toLowerCase().trim();
                const esPagado = ['pagado_completo', 'pagado', 'pagado_totalmente'].includes(est);
                const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
                return !isExtra && !isAgua && !esPagado && montoPendiente > 0;
            })
            .sort((a, b) => {
                let anioA = Number(a.anio || a.año);
                let anioB = Number(b.anio || b.año);
                let mesA = Number(a.mes || a.mesNumero);
                let mesB = Number(b.mes || b.mesNumero);

                if (!anioA && a.id && a.id.includes('-')) {
                    const parts = a.id.replace('his-', '').split('-');
                    anioA = Number(parts[0]) || 2026;
                    mesA = Number(parts[1]) || 1;
                }
                if (!anioB && b.id && b.id.includes('-')) {
                    const parts = b.id.replace('his-', '').split('-');
                    anioB = Number(parts[0]) || 2026;
                    mesB = Number(parts[1]) || 1;
                }

                if (anioA !== anioB) return (anioA || 0) - (anioB || 0);
                return (mesA || 0) - (mesB || 0);
            });
    }, [mesesPendientesData]);

    // Agrupación de meses de mantenimiento por año
    const mantenimientoAgrupadoPorAnio = useMemo(() => {
        const grupos = {};
        mesesMantenimientoPendientesList.forEach(m => {
            let anio = Number(m.anio || m.año);
            let mesNum = Number(m.mes || m.mesNumero);

            if (!anio && m.id && m.id.includes('-')) {
                const parts = m.id.replace('his-', '').split('-');
                anio = Number(parts[0]);
                mesNum = Number(parts[1]);
            }
            if (!anio) anio = 2026;

            if (!grupos[anio]) {
                grupos[anio] = {
                    anio,
                    totalMontoPendiente: 0,
                    totalMontoOriginal: 0,
                    totalMontoPagado: 0,
                    meses: []
                };
            }

            const montoOriginal = Number(m.montoOriginal ?? m.cuotaMensual ?? m.monto ?? 0);
            const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
            const montoPagado = Number(m.montoPagado ?? 0);
            const mesNombre = m.mesNombre || getMesName(mesNum || 1);

            grupos[anio].totalMontoPendiente += montoPendiente;
            grupos[anio].totalMontoOriginal += montoOriginal;
            grupos[anio].totalMontoPagado += montoPagado;
            grupos[anio].meses.push({
                id: m.id,
                mesNumero: mesNum,
                mesNombre: mesNombre.charAt(0).toUpperCase() + mesNombre.slice(1),
                montoOriginal,
                montoPagado,
                montoPendiente,
                estado: m.estado || 'pendiente',
                observaciones: m.observaciones || ''
            });
        });

        // Retornar años ordenados cronológicamente
        return Object.values(grupos).sort((a, b) => a.anio - b.anio);
    }, [mesesMantenimientoPendientesList]);

    // Cuotas extraordinarias detalladas
    const cuotasExtrasDetalladas = useMemo(() => {
        return cuotasExtrasPendientesList
            .map(m => {
                let concepto = m.concepto || m.descripcion || m.id || 'Cuota Extraordinaria';
                let anio = Number(m.anio || m.año || 0);
                if (!anio && m.id) {
                    const matchAnio = m.id.match(/\b(20\d\d)\b/);
                    if (matchAnio) anio = Number(matchAnio[1]);
                }
                const montoOriginal = Number(m.montoOriginal ?? m.monto ?? 0);
                const montoPendiente = Number(m.montoPendiente ?? m.monto ?? 0);
                const montoPagado = Number(m.montoPagado || 0);

                return {
                    id: m.id,
                    concepto,
                    anio: anio || null,
                    montoOriginal,
                    montoPagado,
                    montoPendiente,
                    estado: m.estado || 'pendiente',
                    descripcion: m.descripcion || ''
                };
            })
            .sort((a, b) => {
                const anioA = a.anio || 9999;
                const anioB = b.anio || 9999;
                return anioA - anioB;
            });
    }, [cuotasExtrasPendientesList]);

    const adeudoCapitalMantenimiento = Number(estadoCuenta?.adeudoCapital || 0);
    const adeudoInteresesMantenimiento = Number(estadoCuenta?.adeudoIntereses ?? estadoCuenta?.adeudoInteres ?? 0);
    const saldoFavorCuenta = Number(estadoCuenta?.saldoFavor || 0);

    // Si tiene adeudo, sumamos adeudoCapital + adeudoIntereses + totalMultas + totalCuotasExtras + montoAguaPendiente y restamos el saldo a favor
    const totalAPagarFinDeMes = Math.max(0, Math.round((
        adeudoCapitalMantenimiento + 
        adeudoInteresesMantenimiento + 
        totalMultasPendientes + 
        totalCuotasExtrasPendientes + 
        (tieneAguaPendiente ? montoAguaPendiente : 0) - 
        saldoFavorCuenta
    ) * 100) / 100);

    // LOGS DE VERIFICACIÓN CONTABLE Y CONFIGURACIÓN (Sección Separada)
    if (estadoCuenta) {
        console.log('=== [ESTADO DE CUENTA - RESIDENTE] ===', {
            casa: estadoCuenta.numeroCasa || 'N/A',
            propietario: estadoCuenta.propietario || 'N/A',
            conceptosConsiderados: {
                adeudoMantenimientoCapital: adeudoCapitalMantenimiento,
                adeudoInteresesMantenimiento: adeudoInteresesMantenimiento,
                saldoFavorCuenta: saldoFavorCuenta,
                totalMultasPendientes: totalMultasPendientes,
                totalCuotasExtrasPendientes: totalCuotasExtrasPendientes,
                adeudoAguaPendiente: tieneAguaPendiente ? montoAguaPendiente : 0,
                desgloseCuotasExtrasPendientes: mesesPendientesData
                    .filter(m => m.estado !== 'pagado_completo' && m.estado !== 'pagado_totalmente' && (m.id?.startsWith('EXT-') || m.tipo === 'cuota_extraordinaria'))
                    .map(m => ({ id: m.id, concepto: m.concepto || m.descripcion || 'Cuota Extra', montoPendiente: m.montoPendiente ?? m.monto ?? 0 })),
                desgloseMultasPendientes: multasAdministrativas.map(m => ({ id: m.id, descripcion: m.multaDescripcion, monto: m.monto }))
            },
            totalAPagarCalculado: totalAPagarFinDeMes
        });
    }

    if (configFinanciera) {
        console.log('=== [CONFIGURACIÓN DE COBROS - FRACCIONAMIENTO] ===', {
            fraccionamientoId: fraccionamientoId,
            cuotaMantenimientoMensual: configFinanciera.montoMensual || configFinanciera.cuotaMensual || 0,
            mesesGraciaPermitidos: configFinanciera.interesesMesesPermitidosDeuda || configFinanciera.mesesPermitidos || 1,
            tasaInteresRecargos: configFinanciera.interesPorcentajeMora || 0,
            multasConfiguradas: configFinanciera.multasAdministrativasList || [],
            cuotasExtrasConfiguradas: configFinanciera.cuotaExtras || []
        });
    }

    // Renderizar recibo oficial para visualización e impresión del residente
    const renderReceipt = (ingreso) => {
        if (!ingreso) return null;
        const casa = {
            numero: resolvedCasa?.numero || ingreso.casaNumero || ingreso.numeroCasa || '---',
            propietario: resolvedCasa?.propietario || resolvedCasa?.nombre_residente || resolvedCasa?.nombre || userData?.displayName || userData?.nombre || '---',
            email: resolvedCasa?.email || userData?.email || '---',
            telefono: resolvedCasa?.telefono || userData?.telefono || '---',
            ...resolvedCasa
        };
        const cfg = recibosConfig || {};
        const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: cfg.moneda || 'MXN' }).format(n || 0);
        const total = ingreso.montoNeto || ingreso.monto || 0;

        const toPublicUrl = (url) => {
            if (!url || typeof url !== 'string') return '';
            try {
                const p = new URL(url);
                if (!p.hostname.includes('firebasestorage')) return url;
                p.search = '?alt=media';
                return p.toString();
            } catch {
                return url.split('?')[0] + '?alt=media';
            }
        };

        const numeroALetras = (num = 0) => {
            const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
            const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
            const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
            const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
            const toWords = (n) => {
                let w = '';
                const c = Math.floor(n / 100); const d = Math.floor((n % 100) / 10); const u = n % 10;
                if (c) w += (c === 1 && d === 0 && u === 0 ? 'CIEN' : centenas[c]) + ' ';
                if (d === 1) w += decenas[u] + ' ';
                else if (d === 2) w += (u === 0 ? 'VEINTE' : 'VEINTI' + unidades[u].toLowerCase()) + ' ';
                else if (d > 2) { w += tens[d]; if (u) w += ' Y ' + unidades[u]; w += ' '; }
                else if (u) w += unidades[u] + ' ';
                return w.trim();
            };
            const miles = Math.floor(num / 1000); const resto = num % 1000;
            let res = '';
            if (miles) res += (miles === 1 ? 'MIL' : toWords(miles) + ' MIL') + ' ';
            if (resto || num === 0) res += toWords(resto);
            return res.trim();
        };
        const totalLetras = `${numeroALetras(Math.floor(total))} ${cfg.moneda === 'USD' ? 'DÓLARES' : 'PESOS MEXICANOS'} ${(total % 1).toFixed(2).split('.')[1] || '00'}/100`;

        return (
            <div className="receipt-preview">
                <div className="preview-row preview-top">
                    <div>
                        {cfg.logoUrl ? <img src={toPublicUrl(cfg.logoUrl)} alt="Logo" className="preview-logo" /> : <div className="preview-logo placeholder">Logo</div>}
                        <div className="preview-condo">
                            <h4>{cfg.condominioNombre || configFinanciera?.nombreFraccionamiento || 'Condominio'}</h4>
                            <p>{cfg.condominioDireccion || ''}</p>
                        </div>
                    </div>
                    <div className="preview-right">
                        <h3 className="preview-title">RECIBO DE PAGO</h3>
                        <div className="preview-meta">
                            <span>Folio: {ingreso.folio || '---'}</span>
                            <span>Fecha de pago: {ingreso.fechaPagoOriginal ? (() => {
                                const [y, m, d] = ingreso.fechaPagoOriginal.split('T')[0].split('-');
                                return `${d}/${m}/${y}`;
                            })() : ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')}</span>
                            <span>Fecha de emisión: {ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')} {ingreso.fecha?.toDate?.().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Datos de Facturación</h5>
                    <p><strong>Razón Social:</strong> {cfg.razonSocial || '---'}</p>
                    <p><strong>RFC:</strong> {cfg.rfc || '---'}</p>
                    {cfg.datosFacturacion && <p className="preview-muted">{cfg.datosFacturacion}</p>}
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Administrador</h5>
                    <p><strong>Nombre:</strong> {cfg.administradorNombre || '---'}</p>
                    <p><strong>Dirección:</strong> {cfg.adminDireccion || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Residente</h5>
                    <p><strong>Casa:</strong> {casa.numero || '---'}</p>
                    <p><strong>Nombre:</strong> {casa.propietario || casa.nombre_residente || '---'}</p>
                    <p><strong>Email:</strong> {casa.email || '---'}</p>
                    <p><strong>Teléfono:</strong> {casa.telefono || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-metodo-pago">
                    <div className="preview-block">
                        <p><strong>Método de Pago:</strong> {ingreso.metodoPago || '---'}</p>
                        <p><strong>Referencia:</strong> {ingreso.referencia || '---'}</p>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Conceptos de Pago</h5>
                    <div className="preview-table">
                        <div className="preview-table-header"><span>Concepto</span><span>Importe</span></div>
                        {(() => {
                            const conceptoLower = String(ingreso.concepto || '').toLowerCase();
                            const esPagoAnual = conceptoLower.includes('anual') || ingreso.esPagoAnual;
                            const cuotaRef = Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0
                                ? Math.max(...ingreso.desgloseMensual.map(d => Number(d.importe || 0)))
                                : 0;

                            const partidasArray = (Array.isArray(ingreso.pagos) && ingreso.pagos.length > 0)
                                ? ingreso.pagos
                                : (Array.isArray(ingreso.desgloseConceptos) && ingreso.desgloseConceptos.length > 0)
                                    ? ingreso.desgloseConceptos
                                    : null;

                            if (partidasArray) {
                                return partidasArray.map((item, idx) => {
                                    let detalleText = item.detalle;
                                    if (!detalleText && Array.isArray(item.desgloseMensual) && item.desgloseMensual.length > 0) {
                                        detalleText = item.desgloseMensual.map(d => `${d.mes} ${d.año || d.anio || ''}${d.esParcial ? ' (Parcial)' : ''}`).join(', ');
                                    }
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{item.concepto} {detalleText ? `· ${detalleText}` : ''}</span>
                                            <span>{formatMoney(item.montoNeto || item.monto)}</span>
                                        </div>
                                    );
                                });
                            }

                            if (Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0) {
                                const nombreConcepto = esPagoAnual
                                    ? 'Cuota de Mantenimiento'
                                    : (ingreso.concepto || 'Cuota de Mantenimiento');

                                return ingreso.desgloseMensual.map((item, idx) => {
                                    const anioStr = item.año || item.anio || item.year || '';
                                    const esParcialItem = item.esParcial === true || (cuotaRef > 0 && Number(item.importe) < cuotaRef);
                                    const sufijoEstado = esParcialItem ? ' · Pagado Parcial' : ' · Pagado';
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{nombreConcepto} · {item.mes} {anioStr}{sufijoEstado}</span>
                                            <span>{formatMoney(item.importe)}</span>
                                        </div>
                                    );
                                });
                            }

                            if (ingreso.mesesAplicados && ingreso.mesesAplicados.length > 0) {
                                return ingreso.mesesAplicados.map((item, idx) => {
                                    const labelEstado = item.estado === 'pagado_completo' ? 'Pagado' : (item.estado === 'pagado_parcialmente' ? 'Pagado Parcial' : 'Abonado');
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{ingreso.concepto || 'Cuota de Mantenimiento'} · {item.nombreMes} {item.anio} · {labelEstado}</span>
                                            <span>{formatMoney(item.montoAplicado)}</span>
                                        </div>
                                    );
                                });
                            }

                            return (
                                <div className="preview-table-row">
                                    <span>{ingreso.concepto}</span>
                                    <span>{formatMoney(ingreso.monto)}</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {ingreso.observaciones && ingreso.observaciones.trim() && (
                    <>
                        <hr className="preview-divider" />
                        <div className="preview-block">
                            <h5>Observaciones</h5>
                            <p style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{ingreso.observaciones}</p>
                        </div>
                    </>
                )}

                {(() => {
                    const aplicadoAIntereses = Number(ingreso.aplicadoAIntereses || 0);
                    const aplicadoACapital = Number(ingreso.aplicadoACapital || 0);
                    const aplicadoAAgua = Number(ingreso.aplicadoAAgua || 0);
                    const aplicadoAExtras = Number(ingreso.aplicadoAExtras || 0);
                    const aplicadoAMultas = Number(ingreso.aplicadoAMultas || 0);

                    const adeudoInteresesNuevo = Number(ingreso.adeudoInteresesNuevo ?? 0);
                    const adeudoCapitalNuevo = Number(ingreso.adeudoCapitalNuevo ?? 0);
                    const adeudoAguaNuevo = Number(ingreso.adeudoAguaNuevo ?? 0);
                    const cuotasExtrasNuevo = Number(ingreso.cuotasExtrasNuevo ?? 0);
                    const adeudoMultasNuevo = Number(ingreso.adeudoMultasNuevo ?? 0);
                    const saldoFavorNuevo = Number(ingreso.saldoFavorNuevo ?? 0);

                    const adeudoNuevo = Number(ingreso.adeudoNuevo || 0);
                    const saldoFavorGenerado = Number(ingreso.saldoFavorGenerado || 0);

                    const esTesoreriaRecibo = !esPartidaContableResidente(ingreso);
                    if (esTesoreriaRecibo) {
                        return (
                            <>
                                <hr className="preview-divider" />
                                <div className="preview-block" style={{ backgroundColor: '#fefce8', border: '1px solid #fef08a', borderRadius: '8px', padding: '12px', margin: '10px 0' }}>
                                    <h5 style={{ margin: '0 0 6px 0', color: '#854d0e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        💼 Ingreso de Tesorería
                                    </h5>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#713f12', lineHeight: '1.4' }}>
                                        Este comprobante ampara conceptos de tesorería general del condominio. No afecta ni amortiza el adeudo de mantenimiento de la vivienda ni genera saldo a favor.
                                    </p>
                                </div>
                            </>
                        );
                    }

                    const tieneDesglose = aplicadoAIntereses > 0 || aplicadoACapital > 0 || aplicadoAAgua > 0 || aplicadoAExtras > 0 || aplicadoAMultas > 0 || adeudoNuevo > 0 || saldoFavorGenerado > 0 || adeudoInteresesNuevo > 0 || adeudoCapitalNuevo > 0 || adeudoAguaNuevo > 0 || cuotasExtrasNuevo > 0 || adeudoMultasNuevo > 0 || saldoFavorNuevo > 0;
                    if (!tieneDesglose) return null;
                    return (
                        <>
                            <hr className="preview-divider" />
                            <div className="preview-block">
                                <h5>Aplicación del Pago</h5>
                                <div className="preview-table">
                                    <div className="preview-table-header"><span>Detalle</span><span>Monto</span></div>
                                    {aplicadoAIntereses > 0 && <div className="preview-table-row"><span>Pago a intereses</span><span>{formatMoney(aplicadoAIntereses)}</span></div>}
                                    {aplicadoACapital > 0 && <div className="preview-table-row"><span>Pago a capital</span><span>{formatMoney(aplicadoACapital)}</span></div>}
                                    {aplicadoAAgua > 0 && <div className="preview-table-row"><span>Pago a Agua Potable</span><span>{formatMoney(aplicadoAAgua)}</span></div>}
                                    {aplicadoAExtras > 0 && <div className="preview-table-row"><span>Pago a Cuotas Extraordinarias</span><span>{formatMoney(aplicadoAExtras)}</span></div>}
                                    {aplicadoAMultas > 0 && <div className="preview-table-row"><span>Pago a Sanciones Admin.</span><span>{formatMoney(aplicadoAMultas)}</span></div>}

                                    {ingreso.esPagoAnticipo && saldoFavorGenerado > 0 ? (() => {
                                        const sfNuevo = Number(ingreso.saldoFavorNuevo || 0);
                                        const sfAnterior = Math.max(0, sfNuevo - saldoFavorGenerado);
                                        return (
                                            <>
                                                {sfAnterior > 0 && (
                                                    <div className="preview-table-row" style={{ color: '#059669' }}>
                                                        <span>Saldo a favor</span>
                                                        <span>{formatMoney(sfAnterior)}</span>
                                                    </div>
                                                )}
                                                <div className="preview-table-row" style={{ color: '#059669' }}>
                                                    <span>Nuevo pago ({ingreso.concepto})</span>
                                                    <span>+ {formatMoney(ingreso.montoNeto || ingreso.monto)}</span>
                                                </div>
                                                <div className="preview-table-row" style={{ color: '#059669', fontWeight: 700 }}>
                                                    <span>Total saldo a favor</span>
                                                    <span>{formatMoney(sfNuevo || (sfAnterior + (ingreso.montoNeto || ingreso.monto || 0)))}</span>
                                                </div>
                                            </>
                                        );
                                    })()
                                        : saldoFavorGenerado > 0 ? (
                                            <div className="preview-table-row" style={{ color: '#059669' }}>
                                                <span>Saldo a favor generado</span>
                                                <span>{formatMoney(saldoFavorGenerado)}</span>
                                            </div>
                                        ) : null}

                                    {adeudoCapitalNuevo > 0 && <div className="preview-table-row" style={{ color: '#c2410c' }}><span>Adeudo restante (capital)</span><span>{formatMoney(adeudoCapitalNuevo)}</span></div>}
                                    {adeudoInteresesNuevo > 0 && <div className="preview-table-row" style={{ color: '#c2410c' }}><span>Adeudo restante (intereses)</span><span>{formatMoney(adeudoInteresesNuevo)}</span></div>}
                                    {adeudoAguaNuevo > 0 && <div className="preview-table-row" style={{ color: '#0e7490' }}><span>Adeudo restante (Agua Potable)</span><span>{formatMoney(adeudoAguaNuevo)}</span></div>}
                                    {cuotasExtrasNuevo > 0 && <div className="preview-table-row" style={{ color: '#6d28d9' }}><span>Adeudo restante (Cuotas Extraordinarias)</span><span>{formatMoney(cuotasExtrasNuevo)}</span></div>}
                                    {adeudoMultasNuevo > 0 && <div className="preview-table-row" style={{ color: '#b45309' }}><span>Adeudo restante (Sanciones Admin.)</span><span>{formatMoney(adeudoMultasNuevo)}</span></div>}

                                    {saldoFavorNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#059669', fontWeight: 700 }}>
                                            <span>Saldo a favor restante</span>
                                            <span>{formatMoney(saldoFavorNuevo)}</span>
                                        </div>
                                    )}
                                    {adeudoNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#b91c1c', fontWeight: 700 }}>
                                            <span>Adeudo total restante</span>
                                            <span>{formatMoney(adeudoNuevo)}</span>
                                        </div>
                                    )}
                                    {adeudoNuevo === 0 && saldoFavorNuevo === 0 && (
                                        <div className="preview-table-row" style={{ color: '#16a34a', fontWeight: 700 }}>
                                            <span>Estado financiero</span>
                                            <span>¡Al Corriente! ✓</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    );
                })()}

                <hr className="preview-divider" />
                <div className="preview-total"><span>TOTAL</span><span className="preview-total-amount">{formatMoney(total)}</span></div>
                <p className="preview-leyenda" style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>SON: {totalLetras}</p>
                {
                    (ingreso.esPagoAnual || ingreso.esPagoAnticipo) && (
                        <p className="preview-disclaimer" style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            textAlign: 'center',
                            margin: '16px 0',
                            padding: '12px',
                            backgroundColor: '#fef3c7',
                            border: '2px solid #f59e0b',
                            borderRadius: '4px',
                            color: '#92400e'
                        }}>
                            EL PAGO DE ESTE RECIBO NO LO LIBERA DE ADEUDOS ANTERIORES, CUALQUIER DUDA O ACLARACIÓN FAVOR DE COMUNICARSE CON LA ADMINISTRACIÓN.
                        </p>
                    )
                }
                <div className="preview-footer">
                    <div className="preview-firma-stack">
                        {cfg.selloUrl && <img src={toPublicUrl(cfg.selloUrl)} alt="Sello" className="preview-sello" />}
                        <div className="preview-firma"><div className="preview-firma-line" /><small>Firma y Sello</small><span>{cfg.administradorNombre || '---'}</span></div>
                    </div>
                </div>
            </div>
        );
    };

    const handleDownloadPrint = () => {
        if (receiptRef.current) {
            const w = window.open('', '_blank');
            if (!w) {
                alert('Por favor habilita las ventanas emergentes en tu navegador para descargar o imprimir el recibo.');
                return;
            }
            const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                .map(style => style.outerHTML)
                .join('');

            w.document.write(`
            <html>
                <head>
                    <title>Recibo de Pago - ${reciboSeleccionado?.folio || ''}</title>
                    <meta charset="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                    ${styles}
                    <style>
                        body { background-color: white; margin: 0; padding: 20px; }
                        @media print {
                            body { margin: 0; padding: 10px; }
                            .modal-close-btn, .receipt-actions { display: none !important; }
                        }
                    </style>
                </head>
                <body>
                    <div style="display: flex; justify-content: center;">
                        ${receiptRef.current.innerHTML}
                    </div>
                    <script>
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                            }, 500);
                        };
                    </script>
                </body>
            </html>
            `);
            w.document.close();
        }
    };

    return (
        <div className="finanzas-usuario-page">
            <div className="finanzas-page-banner">
                <div className="finanzas-banner-icon">
                    <FaWallet />
                </div>
                <div className="finanzas-banner-content">
                    <h1 className="finanzas-banner-title">Mis Finanzas</h1>
                    <p className="finanzas-banner-subtitle">Estado de cuenta y recibos de pago</p>
                </div>
            </div>

            {/* Estado de Cuenta - Card con Header de Color */}
            <div className="finanzas-usuario-card">
                <div className="finanzas-usuario-card-header" style={{
                    background: (estadoCuenta?.saldoFavor > 0 ? false : estadoCuenta?.moroso)
                        ? 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
                        : 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
                }}>
                    <div className="finanzas-usuario-card-header-content">
                        <div className="finanzas-usuario-card-icon">
                            <FaWallet />
                        </div>
                        <div className="finanzas-usuario-card-title-wrapper">
                            <h3>Estado de Cuenta</h3>
                            <p>Información financiera de tu vivienda</p>
                        </div>
                        <div className="finanzas-usuario-header-actions">
                            {modulos?.asistente_virtual_ia !== false && modulos?.comunidad !== false && (
                                <button
                                    type="button"
                                    className="btn-subir-comprobante-header"
                                    onClick={() => setModalSubirComprobanteOpen(true)}
                                    title="Subir comprobante de pago para validación"
                                >
                                    <FaPaperclip className="btn-clip-icon" /> <span className="btn-subir-texto">Subir Comprobante</span>
                                </button>
                            )}
                            <div className="finanzas-usuario-card-badge">
                            {(() => {
                                const adeudoReal = Number(estadoCuenta?.adeudo || 0);
                                const saldoFavorReal = Number(estadoCuenta?.saldoFavor || 0);
                                const adeudoNeto = Math.max(0, adeudoReal - saldoFavorReal);
                                const esMorosoVisible = (saldoFavorReal > 0 ? false : (estadoCuenta?.moroso && adeudoNeto > 0));
                                return esMorosoVisible ? 'MOROSO' : 'AL CORRIENTE';
                            })()}
                        </div>
                        </div>
                    </div>
                </div>
                <div className="finanzas-usuario-card-body">
                    {loadingEstado ? (
                        <p className="loading-text">Cargando estado de cuenta...</p>
                    ) : estadoCuenta ? (
                        <div className="estado-info-grid">
                            {/* Fila 1 */}
                            <div className="info-item-modern-box">
                                <div className="info-box-header">
                                    <FaHome className="info-box-icon" />
                                    <span className="info-box-label">CASA Y PROPIETARIO</span>
                                </div>
                                <span className="info-box-value" style={{ fontSize: '1.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {estadoCuenta.numeroCasa} - {estadoCuenta.propietario}
                                </span>
                            </div>
                            {(() => {
                                const mostrarSaldoFavor = saldoFavorCuenta > 0 && totalAPagarFinDeMes <= 0;
                                const label = mostrarSaldoFavor ? 'SALDO A FAVOR' : 'ADEUDO MANTENIMIENTO';
                                const valueClass = mostrarSaldoFavor ? 'saldo-favor' : adeudoCapitalMantenimiento > 0 ? 'adeudo-pendiente' : 'sin-adeudo';
                                const displayValue = mostrarSaldoFavor ? formatCurrency(saldoFavorCuenta) : formatCurrency(adeudoCapitalMantenimiento);
                                const tieneMesesDesglose = !mostrarSaldoFavor && adeudoCapitalMantenimiento > 0 && mesesMantenimientoPendientesList.length > 0;

                                return (
                                    <div className="info-item-modern-box">
                                        <div className="info-box-header">
                                            <FaDollarSign className="info-box-icon" />
                                            <span className="info-box-label">{label}</span>
                                        </div>
                                        <span className={`info-box-value-large ${valueClass}`}>
                                            {displayValue}
                                        </span>
                                        {tieneMesesDesglose && (
                                            <button
                                                type="button"
                                                className="btn-desglose-chip mantto"
                                                onClick={() => {
                                                    setTabDesglose('mantenimiento');
                                                    setMostrarModalDesglose(true);
                                                }}
                                                title="Ver desglose mes con mes y año por año"
                                            >
                                                <FaListUl className="btn-chip-icon" />
                                                <span>Ver desglose ({mesesMantenimientoPendientesList.length} meses)</span>
                                                <FaChevronRight className="btn-chip-arrow" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })()}
                            <div className="info-item-modern-box">
                                <div className="info-box-header">
                                    <FaDollarSign className="info-box-icon" style={{ color: totalAPagarFinDeMes > 0 ? '#ef4444' : '#10b981' }} />
                                    <span className="info-box-label">TOTAL A PAGAR A FIN DE MES</span>
                                </div>
                                <span className={`info-box-value-large ${totalAPagarFinDeMes > 0 ? 'adeudo-pendiente' : 'sin-adeudo'}`}>
                                    {formatCurrency(totalAPagarFinDeMes)}
                                </span>
                            </div>

                            {/* Intereses Pendientes */}
                            <div className="info-item-modern-box">
                                <div className="info-box-header">
                                    <FaPercentage className="info-box-icon" style={{ color: '#f59e0b' }} />
                                    <span className="info-box-label">INTERESES PENDIENTES</span>
                                </div>
                                <span className={`info-box-value-large ${adeudoInteresesMantenimiento > 0 ? 'adeudo-pendiente' : 'sin-adeudo'}`}>
                                    {formatCurrency(adeudoInteresesMantenimiento)}
                                </span>
                            </div>

                            {/* Fila 2 */}
                            {modulos.consumo_agua_potable !== false && (
                                <div className="info-item-modern-box">
                                    <div className="info-box-header">
                                        <FaWater className="info-box-icon" style={{ color: '#0ea5e9' }} />
                                        <span className="info-box-label">AGUA POTABLE</span>
                                    </div>
                                    <span className={`info-box-value-large ${tieneAguaPendiente ? 'adeudo-pendiente' : 'saldo-favor'}`} style={{ fontSize: tieneAguaPendiente ? '1.15rem' : '1.3rem' }}>
                                        {tieneAguaPendiente ? `${formatCurrency(montoAguaPendiente)} Pendiente` : 'Al corriente'}
                                    </span>
                                </div>
                            )}
                            <div className="info-item-modern-box" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '80px' }}>
                                <div>
                                    <div className="info-box-header">
                                        <FaCalendarAlt className="info-box-icon" style={{ color: '#f59e0b' }} />
                                        <span className="info-box-label">CUOTAS EXTRAORDINARIAS</span>
                                    </div>
                                    <span className={`info-box-value-large ${totalCuotasExtrasPendientes > 0 ? 'adeudo-pendiente' : 'sin-adeudo'}`}>
                                        {formatCurrency(totalCuotasExtrasPendientes)}
                                    </span>
                                </div>
                                {totalCuotasExtrasPendientes > 0 && cuotasExtrasDetalladas.length > 0 && (
                                    <button
                                        type="button"
                                        className="btn-desglose-chip extras"
                                        onClick={() => {
                                            setTabDesglose('extraordinarias');
                                            setMostrarModalDesglose(true);
                                        }}
                                        title="Ver desglose de cuotas extraordinarias"
                                    >
                                        <FaCalendarAlt className="btn-chip-icon" />
                                        <span>Ver desglose ({cuotasExtrasDetalladas.length} cuotas)</span>
                                        <FaChevronRight className="btn-chip-arrow" />
                                    </button>
                                )}
                            </div>
                            {modulos?.residente_mostrar_sanciones !== false && (
                                <div className="info-item-modern-box">
                                    <div className="info-box-header">
                                        <FaGavel className="info-box-icon" style={{ color: '#ec4899' }} />
                                        <span className="info-box-label">SANCIONES ADMINISTRATIVAS</span>
                                    </div>
                                    <span className={`info-box-value-large ${totalMultasPendientes > 0 ? 'adeudo-pendiente' : 'sin-adeudo'}`}>
                                        {formatCurrency(totalMultasPendientes)}
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="loading-text">No se encontró información de tu vivienda.</p>
                    )}

                    {/* Sanciones Administrativas dentro de Estado de Cuenta */}
                    {modulos?.residente_mostrar_sanciones !== false && !loadingMultas && multasAdministrativas.length > 0 && (
                        <div className="multas-premium-section">
                            <div className="multas-premium-header">
                                <div className="multas-title-group">
                                    <FaGavel className="multas-gavel-icon" />
                                    <h4>Sanciones Administrativas</h4>
                                </div>
                                <div className="multas-header-actions">
                                    <span className="multas-counter-badge">{multasAdministrativas.length}</span>
                                    {multasAdministrativas.length > 3 && (
                                        <button
                                            type="button"
                                            className="multas-toggle-minimal"
                                            onClick={() => setMostrarDetalleMultas(!mostrarDetalleMultas)}
                                        >
                                            {mostrarDetalleMultas ? <FaChevronUp /> : <FaChevronDown />}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="multas-premium-list">
                                {(mostrarDetalleMultas ? multasAdministrativas : multasAdministrativas.slice(0, 3)).map((multa) => {
                                    const fechaFalta = multa.fechaFalta?.toDate ? multa.fechaFalta.toDate() : new Date(multa.fechaFalta);

                                    return (
                                        <div key={multa.id} className="multa-premium-card">
                                            <div className="multa-card-left">
                                                <div className="multa-icon-circle">
                                                    <FaGavel />
                                                </div>
                                                <div className="multa-card-info">
                                                    <div className="multa-card-desc">{multa.multaDescripcion || 'Sanción administrativa'}</div>
                                                    <div className="multa-card-meta">
                                                        <span className="multa-meta-item">
                                                            <FaCalendarAlt /> {fechaFalta.toLocaleDateString('es-MX')}
                                                        </span>
                                                        {multa.comentarios && (
                                                            <span className="multa-meta-comment">{multa.comentarios}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="multa-card-right">
                                                <span className="multa-card-amount">{formatCurrency(multa.monto || 0)}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {modulos?.residente_mostrar_sanciones !== false && loadingMultas && (
                        <div className="multas-estado-cuenta-section">
                            <p className="loading-text" style={{ fontSize: '0.9rem', color: '#6b7280' }}>Cargando sanciones administrativas...</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Configuración Financiera del Fraccionamiento */}
            <div className="finanzas-usuario-card">
                <div className="finanzas-usuario-card-header" style={{
                    background: 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)'
                }}>
                    <div className="finanzas-usuario-card-header-content">
                        <div className="finanzas-usuario-card-icon">
                            <FaInfoCircle />
                        </div>
                        <div className="finanzas-usuario-card-title-wrapper">
                            <h3>Información de Configuración de Cobros</h3>
                            <p>Revisa la configuración financiera de tu fraccionamiento</p>
                        </div>
                    </div>
                </div>
                <div className="finanzas-usuario-card-body">
                    {loadingConfig ? (
                        <p className="loading-text">Cargando configuración...</p>
                    ) : configFinanciera ? (
                        <div className="config-content-modern">
                            <div className="estado-info-grid">
                                {modulos?.residente_mostrar_cuota_mensual !== false && (
                                    <div className="info-item-modern-box">
                                        <div className="info-box-header">
                                            <FaDollarSign className="info-box-icon" />
                                            <span className="info-box-label">CUOTA MENSUAL</span>
                                        </div>
                                        <span className="info-box-value highlight">{formatCurrency(configFinanciera.montoMensual)}</span>
                                    </div>
                                )}
                                {modulos?.residente_mostrar_corte_dia !== false && (
                                    <div className="info-item-modern-box">
                                        <div className="info-box-header">
                                            <FaCalendarAlt className="info-box-icon" />
                                            <span className="info-box-label">DÍA DE CORTE</span>
                                        </div>
                                        <span className="info-box-value">{configFinanciera.corteDiaResidentes || configFinanciera.corteDia} de cada mes</span>
                                    </div>
                                )}
                                {modulos?.residente_mostrar_intereses !== false && (
                                    <>
                                        <div className="info-item-modern-box">
                                            <div className="info-box-header">
                                                <FaExclamationCircle className="info-box-icon" />
                                                <span className="info-box-label">INICIO DE INTERESES</span>
                                            </div>
                                            <span className="info-box-value">Día {configFinanciera.sancionesInicioDia}</span>
                                        </div>
                                        <div className="info-item-modern-box">
                                            <div className="info-box-header">
                                                <FaPercentage className="info-box-icon" />
                                                <span className="info-box-label">ESTADO DE INTERESES</span>
                                            </div>
                                            <span className={`info-box-value ${configFinanciera.interesesEnabled ? 'highlight' : 'disabled'}`}>
                                                {getInterestStatusLabel(configFinanciera.interesesEnabled)}
                                            </span>
                                        </div>
                                        {configFinanciera.interesesEnabled && configFinanciera.interesesValue > 0 && (() => {
                                            const isPercent = typeof configFinanciera.interesesType === 'string' &&
                                                (configFinanciera.interesesType.includes('percent') || configFinanciera.interesesType.includes('porcentaje'));
                                            const formattedValue = isPercent
                                                ? `${configFinanciera.interesesValue}%`
                                                : formatCurrency(configFinanciera.interesesValue);

                                            return (
                                                <div className="info-item-modern-box">
                                                    <div className="info-box-header">
                                                        {isPercent ? (
                                                            <FaPercentage className="info-box-icon warning" />
                                                        ) : (
                                                            <FaDollarSign className="info-box-icon warning" />
                                                        )}
                                                        <span className="info-box-label">TASA DE INTERÉS</span>
                                                    </div>
                                                    <span className="info-box-value warning">{formattedValue} mensual</span>
                                                </div>
                                            );
                                        })()}
                                    </>
                                )}
                                {modulos?.residente_mostrar_descuento_pronto_pago !== false && (
                                    <div className="info-item-modern-box">
                                        <div className="info-box-header">
                                            <FaPercentage className="info-box-icon" />
                                            <span className="info-box-label">DSCTO. PRONTO PAGO</span>
                                        </div>
                                        <span className={`info-box-value ${configFinanciera.discountEnabled ? 'highlight' : 'disabled'}`}>
                                            {configFinanciera.discountEnabled && configFinanciera.discountPercent > 0
                                                ? `${configFinanciera.discountPercent}%`
                                                : 'Desactivado (OFF)'}
                                        </span>
                                    </div>
                                )}
                                {modulos?.residente_mostrar_cuotas_extras_cobro !== false && (
                                    <div
                                        className={`info-item-modern-box ${configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0 ? 'clickable' : ''}`}
                                        onClick={() => {
                                            if (configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0) {
                                                setMostrarDetalleCuotas(!mostrarDetalleCuotas);
                                            }
                                        }}
                                        style={{ cursor: configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0 ? 'pointer' : 'default' }}
                                    >
                                        <div className="info-box-header">
                                            <FaDollarSign className="info-box-icon" />
                                            <span className="info-box-label">CUOTAS EXTRAS</span>
                                            {configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0 && (
                                                mostrarDetalleCuotas ? <FaChevronUp style={{ marginLeft: 8, fontSize: '0.8rem' }} /> : <FaChevronDown style={{ marginLeft: 8, fontSize: '0.8rem' }} />
                                            )}
                                        </div>
                                        <span className={`info-box-value ${configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0 ? 'highlight' : 'disabled'}`}>
                                            {configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0
                                                ? (configFinanciera.cuotaExtras.length === 1 
                                                    ? `${configFinanciera.cuotaExtras[0].descripcion}: ${formatCurrency(configFinanciera.cuotaExtras[0].monto)}` 
                                                    : `${configFinanciera.cuotaExtras.length} configuradas`)
                                                : 'Ninguna (OFF)'}
                                        </span>
                                    </div>
                                )}
                                {modulos?.residente_mostrar_sanciones !== false && (
                                    <div
                                        className={`info-item-modern-box ${configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0 ? 'clickable' : ''}`}
                                        onClick={() => {
                                            if (configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0) {
                                                setMostrarDetalleMultas(!mostrarDetalleMultas);
                                            }
                                        }}
                                        style={{ cursor: configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0 ? 'pointer' : 'default' }}
                                    >
                                        <div className="info-box-header">
                                            <FaExclamationCircle className="info-box-icon" />
                                            <span className="info-box-label">SANCIONES ADMIN.</span>
                                            {configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0 && (
                                                mostrarDetalleMultas ? <FaChevronUp style={{ marginLeft: 8, fontSize: '0.8rem' }} /> : <FaChevronDown style={{ marginLeft: 8, fontSize: '0.8rem' }} />
                                            )}
                                        </div>
                                        <span className={`info-box-value ${configFinanciera.multasEnabled ? 'highlight' : 'disabled'}`}>
                                            {configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0
                                                ? `${configFinanciera.multasList.length} registrada${configFinanciera.multasList.length > 1 ? 's' : ''}`
                                                : 'Desactivado (OFF)'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {modulos?.residente_mostrar_sanciones !== false && mostrarDetalleMultas && configFinanciera.multasEnabled && configFinanciera.multasList && configFinanciera.multasList.length > 0 && (
                                <div className="multas-detalle-container" style={{ marginTop: '20px' }}>
                                    <div className="multas-detalle-header">
                                        <strong>Sanciones Registradas:</strong>
                                    </div>
                                    <div className="multas-list">
                                        {configFinanciera.multasList.map((multa, index) => (
                                            <div key={multa.id || index} className="multa-item">
                                                <div className="multa-descripcion">
                                                    <span className="multa-numero">{index + 1}.</span>
                                                    <span className="multa-text">{multa.descripcion}</span>
                                                </div>
                                                <div className="multa-monto">
                                                    {formatCurrency(multa.monto)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {modulos?.residente_mostrar_cuotas_extras_cobro !== false && mostrarDetalleCuotas && configFinanciera.cuotaExtras && configFinanciera.cuotaExtras.length > 0 && (
                                <div className="multas-detalle-container" style={{ marginTop: '20px' }}>
                                    <div className="multas-detalle-header" style={{ marginBottom: '16px' }}>
                                        <strong style={{ fontSize: '1.1rem', color: '#1e293b' }}>Cuotas Extraordinarias Activas:</strong>
                                    </div>
                                    <div className="cuotas-extras-grid" style={{ display: 'grid', gap: '16px' }}>
                                        {configFinanciera.cuotaExtras.map((cuota, index) => {
                                            const isApplying = aplicaEsteMes(cuota.mesInicio, cuota.frecuencia);
                                            
                                            // Determinar estado de la cuota extra revisando mesesPendientesData
                                            let estadoExtra = 'no_aplica';
                                            if (isApplying) {
                                                const cleanName = String(cuota.descripcion || 'extra').replace(/\s+/g, '').substring(0, 15);
                                                const mesActualStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                                                const targetId = `EXT-${cleanName}-${mesActualStr}`;
                                                const cuotaEncontrada = mesesPendientesData.find(m => m.id === targetId || m.mesId === targetId);
                                                
                                                if (cuotaEncontrada) {
                                                    estadoExtra = (cuotaEncontrada.estado === 'pagado_completo') ? 'pagada' : 'pendiente';
                                                } else {
                                                    // Fallback si el cron no ha corrido pero la cuota aplica este mes
                                                    estadoExtra = estadoCuenta?.adeudo > 0 ? 'pendiente' : 'pagada';
                                                }
                                            }

                                            // Calcular impacto anual
                                            let impactoAnual = Number(cuota.monto || 0);
                                            switch (cuota.frecuencia) {
                                                case "monthly": impactoAnual *= 12; break;
                                                case "bimonthly": impactoAnual *= 6; break;
                                                case "quarterly": impactoAnual *= 4; break;
                                                case "semiannual": impactoAnual *= 2; break;
                                                case "yearly": impactoAnual *= 1; break;
                                                default: impactoAnual *= 12;
                                            }

                                            // Formato de fecha de creación si es posible
                                            let fechaCreacionStr = "N/D";
                                            if (cuota.id && String(cuota.id).length > 10) {
                                                const d = new Date(cuota.id);
                                                if (!isNaN(d.getTime())) {
                                                    fechaCreacionStr = d.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
                                                }
                                            }

                                            return (
                                                <div key={cuota.id || index} className="cuota-extra-card" style={{
                                                    background: '#ffffff',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '12px',
                                                    padding: '16px',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '12px'
                                                }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div style={{ background: '#f1f5f9', color: '#64748b', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>{index + 1}</div>
                                                            {cuota.descripcion}
                                                        </div>
                                                        <div style={{ fontWeight: 700, color: '#0ea5e9', fontSize: '1.1rem' }}>
                                                            {formatCurrency(cuota.monto)}
                                                        </div>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', fontSize: '0.85rem', color: '#64748b' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaCalendarAlt color="#94a3b8" /> Frecuencia: <strong>{getFrecuenciaText(cuota.frecuencia)}</strong></div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaHistory color="#94a3b8" /> Inicia: <strong>{getMesName(cuota.mesInicio)}</strong></div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaClock color="#94a3b8" /> Próximo: <strong>{calculaSiguienteCobro(cuota.mesInicio, cuota.frecuencia)}</strong></div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><FaPlusCircle color="#94a3b8" /> Agregada: <strong>{fechaCreacionStr}</strong></div>
                                                    </div>

                                                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                        <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                            Impacto anual: <strong>{formatCurrency(impactoAnual)}</strong>
                                                        </div>
                                                        <div>
                                                            {estadoExtra === 'pendiente' ? (
                                                                <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>Pendiente en adeudo</span>
                                                            ) : estadoExtra === 'pagada' ? (
                                                                <span style={{ background: '#dcfce3', color: '#166534', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>Cubierta / Al corriente</span>
                                                            ) : (
                                                                <span style={{ background: '#f1f5f9', color: '#64748b', padding: '4px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600 }}>No aplica este mes</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}


                            <div className="config-note-modern">
                                <small>
                                    💡 Los cargos se aplican automáticamente el día {configFinanciera.corteDiaResidentes || configFinanciera.corteDia} de cada mes.
                                    {configFinanciera.interesesEnabled &&
                                        ` Los intereses se calculan a partir del día ${configFinanciera.sancionesInicioDia}.`
                                    }
                                </small>
                            </div>
                        </div>
                    ) : (
                        <p className="loading-text">No se encontró configuración financiera.</p>
                    )}
                </div>
            </div>

            {/* Historial de Pagos */}
            <div className="finanzas-usuario-card">
                <div className="finanzas-usuario-card-header" style={{
                    background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)'
                }}>
                    <div className="finanzas-usuario-card-header-content">
                        <div className="finanzas-usuario-card-icon">
                            <FaMoneyCheckAlt />
                        </div>
                        <div className="finanzas-usuario-card-title-wrapper">
                            <h3>Pagos Realizados</h3>
                            <p>Historial completo de tus pagos registrados</p>
                        </div>
                        {pagos.length > 0 && (
                            <div className="finanzas-usuario-card-badge">
                                {pagos.length}
                            </div>
                        )}
                    </div>
                </div>
                <div className="finanzas-usuario-card-body">
                    {loadingPagos ? (
                        <p className="loading-text">Cargando pagos...</p>
                    ) : pagos.length === 0 ? (
                        <div className="empty-state">
                            <FaMoneyCheckAlt className="empty-icon" />
                            <p>No se registran pagos</p>
                            <small>Cuando registres un pago en administración aparecerá aquí</small>
                        </div>
                    ) : (
                        <>
                            <div className="pagos-premium-list">
                                {(mostrarTodosPagos ? pagos : pagos.slice(0, 3)).map((pago, index) => (
                                    <div key={pago.id} className={`pago-premium-card ${pago.tipo === 'registro_mensual' ? 'registro-mensual' : ''}`}>
                                        {/* Botón Ojito para ver el recibo oficial en la esquina superior derecha */}
                                        <button
                                            type="button"
                                            className="btn-ver-recibo-card"
                                            onClick={() => {
                                                setReciboSeleccionado(pago);
                                                setShowReceiptModal(true);
                                            }}
                                            title="Ver recibo oficial"
                                            aria-label="Ver recibo oficial"
                                        >
                                            <FaEye />
                                        </button>

                                        {pago.tipo === 'registro_mensual' ? (
                                            /* RENDER ESTRUCTURADO — LAYOUT VERTICAL POR SECCIONES */
                                            <div className="pago-card-main registro-mensual">

                                                {/* SECCIÓN 1: Título + Badge en la misma fila */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', gap: '8px', paddingRight: '40px' }}>
                                                    <h4 className="registro-mensual-title">
                                                        {pago.concepto}
                                                    </h4>
                                                    {index === 0 && <span className="pago-badge-recent" style={{ flexShrink: 0 }}>Más reciente</span>}
                                                </div>

                                                {/* SECCIÓN 2: Metadatos apilados verticalmente */}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100%' }}>
                                                    <div className="pago-meta-item" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            <FaCalendarAlt className="pago-meta-icon" />
                                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDateTime(pago.fecha || pago.timestamp)}</span>
                                                        </span>
                                                        {formatFechaPago(pago) && (
                                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                <span className="meta-label" style={{ marginLeft: '4px' }}>FECHA DE PAGO:</span>
                                                                <span className="meta-value" style={{ color: '#2563eb' }}>{formatFechaPago(pago)}</span>
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        <span className="meta-label">MÉTODO:</span>
                                                        <span className="meta-value">{pago.metodoPago || 'registro_automatico'}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                        <span className="meta-label">REF:</span>
                                                        <span className="meta-value">
                                                            {(() => {
                                                                const dateObj = pago.fecha?.toDate ? pago.fecha.toDate() : (pago.timestamp?.toDate ? pago.timestamp.toDate() : new Date());
                                                                const m = dateObj.getMonth() + 1;
                                                                const y = dateObj.getFullYear();
                                                                return `REG-${m}${y}  EXT-${m}${y}`;
                                                            })()}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* SECCIÓN 3: Columnas Mantto / Extraordinaria */}
                                                <div className="registro-mensual-columns">
                                                    <div className="col-item">
                                                        <span className="col-label">Mantto</span>
                                                        <span className="col-value">{formatCurrency(pago.desglose?.cuotaBase || 0).replace('.00', '')}</span>
                                                    </div>
                                                    <div className="col-item">
                                                        <span className="col-label">Extraordinaria</span>
                                                        <span className="col-value">{formatCurrency(pago.desglose?.cuotasExtras || 0).replace('.00', '')}</span>
                                                    </div>
                                                </div>

                                                {/* SECCIÓN 4: Estado tras el pago */}
                                                <div className="estado-tras-pago-box">
                                                    <span className="subgroup-label">ESTADO TRAS EL PAGO</span>

                                                    {Number(pago.desglose?.adeudoTotal || pago.saldoFinal || 0) > 0 ? (
                                                        <>
                                                            <div className="estado-row">
                                                                <span className="estado-label">Adeudo:</span>
                                                                <span className="estado-value-red">{formatCurrency(pago.desglose?.adeudoTotal || pago.saldoFinal || 0)}</span>
                                                            </div>
                                                            <div className="estado-row">
                                                                <span className="estado-label">Saldo a Favor:</span>
                                                                <span className="estado-value-black">{formatCurrency(pago.desglose?.saldoFavorFinal ?? pago.desglose?.nuevoSaldoFavor ?? pago.saldoFavorNuevo ?? pago.saldoFavorActual ?? 0)}</span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="estado-row">
                                                                <span className="estado-label">Cobrado:</span>
                                                                <span className="estado-value-green">{formatCurrency((pago.desglose?.cuotaBase || 0) + (pago.desglose?.cuotasExtras || 0))}</span>
                                                            </div>
                                                            <div className="estado-row">
                                                                <span className="estado-label">Saldo a Favor:</span>
                                                                <span className="estado-value-green">{formatCurrency(pago.desglose?.saldoFavorFinal ?? pago.desglose?.nuevoSaldoFavor ?? pago.saldoFavorNuevo ?? pago.saldoFavorActual ?? 0)}</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>

                                                {/* SECCIÓN 5: Observaciones */}
                                                {(() => {
                                                    const obsExistente = typeof pago.observaciones === 'string' && pago.observaciones.trim() !== '' ? pago.observaciones.trim() : null;
                                                    const esAgua = String(pago.concepto || '').toLowerCase().includes('agua') || String(pago.concepto || '').toLowerCase().includes('cea');
                                                    let textoNota = obsExistente;
                                                    
                                                    if (!textoNota && esAgua) {
                                                        const adeudoPosterior = Number(pago.adeudoActual || pago.adeudoNuevo || 0);
                                                        if (adeudoPosterior > 0) {
                                                            const formattedAdeudo = formatCurrency(adeudoPosterior);
                                                            textoNota = `Pago de cuota de consumo de agua potable liquidado. Cuenta con adeudo histórico pendiente (${formattedAdeudo}). El pago de consumo de agua potable no libera de adeudos anteriores.`;
                                                        } else {
                                                            textoNota = 'Consumo de Agua Potable - AL CORRIENTE';
                                                        }
                                                    }

                                                    if (!textoNota) return null;

                                                    return (
                                                        <div className="pago-notes-accordion-wrapper" style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f1f5f9', width: '100%' }}>
                                                            <details className="pago-notes-details" style={{ width: '100%' }}>
                                                                <summary style={{ fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', cursor: 'pointer', outline: 'none', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                                                                    <span>NOTAS Y OBSERVACIONES</span>
                                                                    <span style={{ fontSize: '0.7rem', color: '#3b82f6', textTransform: 'none', fontWeight: '600' }}>Ver detalle ▾</span>
                                                                </summary>
                                                                <div className="observaciones-box-zen" style={{ marginTop: '6px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6' }}>
                                                                    <p className="observaciones-text" style={{ margin: 0, fontSize: '0.82rem', color: '#334155', fontStyle: 'italic', lineHeight: '1.4' }}>{textoNota}</p>
                                                                </div>
                                                            </details>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ) : (
                                            /* RENDER PARA PAGO NORMAL */
                                            <>
                                                <div className="pago-card-main">
                                                    <div className="pago-card-info-group">
                                                        <div className="pago-card-title-row" style={{ paddingRight: '40px' }}>
                                                            <h4 className="pago-card-concept">
                                                                {pago.concepto || 'Pago de mantenimiento'}
                                                                {!esPartidaContableResidente(pago) && (
                                                                    <span style={{
                                                                        marginLeft: '8px',
                                                                        fontSize: '0.75rem',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '12px',
                                                                        backgroundColor: '#fef3c7',
                                                                        color: '#b45309',
                                                                        fontWeight: 600,
                                                                        border: '1px solid #fde68a'
                                                                    }}>
                                                                        💼 Tesorería
                                                                    </span>
                                                                )}
                                                            </h4>
                                                            {index === 0 && <span className="pago-badge-recent">Más reciente</span>}
                                                        </div>
                                                        <div className="pago-card-meta">
                                                            <div className="pago-meta-item" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                    <FaCalendarAlt className="pago-meta-icon" />
                                                                    <span>{formatDateTime(pago.fecha || pago.timestamp)}</span>
                                                                </span>
                                                                {formatFechaPago(pago) && (
                                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                        <span className="pago-label" style={{ marginLeft: '4px' }}>FECHA DE PAGO:</span>
                                                                        <span className="pago-value" style={{ color: '#2563eb' }}>{formatFechaPago(pago)}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="pago-meta-item">
                                                                <span className="pago-label">MÉTODO:</span>
                                                                <span className="pago-value">{pago.metodoPago || 'N/A'}</span>
                                                            </div>
                                                            {pago.referencia && (
                                                                <div className="pago-meta-item">
                                                                    <span className="pago-label">REF:</span>
                                                                    <span className="pago-value" style={{ display: 'flex', gap: '8px' }}>
                                                                        <span>{pago.referencia}</span>
                                                                        {pago.cuotasExtraniordinarias && pago.cuotasExtraniordinarias.length > 0 && (
                                                                            <span>EXT-{pago.referencia.split('-')[1] || new Date(pago.fecha?.seconds * 1000).getFullYear()}</span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="pago-card-amount-group">
                                                        {pago.tipo === 'cargo_mensual' && pago.cuotasExtraniordinarias && pago.cuotasExtraniordinarias.length > 0 ? (
                                                            <div style={{ display: 'flex', gap: '24px', textAlign: 'center' }}>
                                                                <div>
                                                                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Cuota mensual</div>
                                                                    <div className="pago-card-amount" style={{ fontSize: '1.2rem', color: '#3b82f6' }}>{formatCurrency(pago.cuotaMensual || pago.cargoCapitalDelMes - pago.montoCuotasExtras)}</div>
                                                                </div>
                                                                <div>
                                                                    <div style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Cuotas extraordinarias</div>
                                                                    <div className="pago-card-amount" style={{ fontSize: '1.2rem', color: '#3b82f6' }}>{formatCurrency(pago.montoCuotasExtras || 0)}</div>
                                                                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '4px' }}>
                                                                        {pago.cuotasExtraniordinarias.map((ext, eIdx) => (
                                                                            <div key={eIdx}>• {ext.descripcion}</div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <span className="pago-card-amount">{formatCurrency(pago.monto || pago.cargoDelMes || 0)}</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="pago-card-details-grid">
                                                    {Array.isArray(pago.pagos) && pago.pagos.length > 1 && (
                                                        <div className="pago-subgroup" style={{ gridColumn: '1 / -1', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px' }}>
                                                            <span className="subgroup-label" style={{ color: '#475569', fontWeight: '700', marginBottom: '6px', display: 'block' }}>DESGLOSE DE CONCEPTOS</span>
                                                            <div className="subgroup-items" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                {pago.pagos.map((item, pIdx) => (
                                                                    <div key={pIdx} className="subgroup-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: pIdx < pago.pagos.length - 1 ? '1px dashed #cbd5e1' : 'none', paddingBottom: '4px' }}>
                                                                        <span style={{ fontSize: '0.82rem', color: '#334155' }}>
                                                                            <strong>{item.concepto}</strong>
                                                                            {item.detalle ? <span style={{ color: '#64748b', fontSize: '0.75rem', marginLeft: '6px' }}>({item.detalle})</span> : null}
                                                                        </span>
                                                                        <strong style={{ color: '#0f172a', fontSize: '0.85rem' }}>{formatCurrency(item.montoNeto || item.monto || 0)}</strong>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {((pago.aplicadoAIntereses > 0 || pago.pagoAplicadoAIntereses > 0) ||
                                                      (pago.aplicadoACapital > 0 || pago.pagoAplicadoACapital > 0) ||
                                                      (pago.aplicadoAAgua > 0 || pago.pagoAplicadoAAgua > 0 || pago.pagoAplicadoAgua > 0) ||
                                                      (pago.aplicadoAExtras > 0 || pago.pagoAplicadoAExtras > 0 || pago.aplicadoAExtraordinarias > 0) ||
                                                      (pago.aplicadoAMultas > 0 || pago.pagoAplicadoAMultas > 0)) && (
                                                        <div className="pago-subgroup">
                                                            <span className="subgroup-label">DESTINO DEL PAGO</span>
                                                            <div className="subgroup-items">
                                                                <div className="subgroup-item">
                                                                    <span>Intereses:</span>
                                                                    <strong>{formatCurrency(pago.aplicadoAIntereses || pago.pagoAplicadoAIntereses || 0)}</strong>
                                                                </div>
                                                                <div className="subgroup-item">
                                                                    <span>Capital:</span>
                                                                    <strong>{formatCurrency(pago.aplicadoACapital || pago.pagoAplicadoACapital || 0)}</strong>
                                                                </div>
                                                                {(Number(pago.aplicadoAAgua || pago.pagoAplicadoAAgua || pago.pagoAplicadoAgua || 0) > 0) && (
                                                                    <div className="subgroup-item">
                                                                        <span>Agua Potable:</span>
                                                                        <strong>{formatCurrency(pago.aplicadoAAgua || pago.pagoAplicadoAAgua || pago.pagoAplicadoAgua || 0)}</strong>
                                                                    </div>
                                                                )}
                                                                {(Number(pago.aplicadoAExtras || pago.pagoAplicadoAExtras || pago.aplicadoAExtraordinarias || 0) > 0) && (
                                                                    <div className="subgroup-item">
                                                                        <span>Cuota Extraordinaria:</span>
                                                                        <strong>{formatCurrency(pago.aplicadoAExtras || pago.pagoAplicadoAExtras || pago.aplicadoAExtraordinarias || 0)}</strong>
                                                                    </div>
                                                                )}
                                                                {(Number(pago.aplicadoAMultas || pago.pagoAplicadoAMultas || 0) > 0) && (
                                                                    <div className="subgroup-item">
                                                                        <span>Multas / Sanciones:</span>
                                                                        <strong>{formatCurrency(pago.aplicadoAMultas || pago.pagoAplicadoAMultas || 0)}</strong>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!esPartidaContableResidente(pago) ? (
                                                        <div className="pago-subgroup" style={{ backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '6px', padding: '8px' }}>
                                                            <span className="subgroup-label" style={{ color: '#b45309' }}>💼 INGRESO DE TESORERÍA</span>
                                                            <div className="subgroup-items">
                                                                <div className="subgroup-item">
                                                                    <span style={{ fontSize: '0.8rem', color: '#92400e' }}>
                                                                        Concepto de tesorería general. No afecta el adeudo de mantenimiento de la vivienda.
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (pago.adeudoActual !== undefined || pago.adeudoNuevo !== undefined || pago.saldoFavorActual !== undefined || pago.saldoFavorNuevo !== undefined) && (
                                                        <div className="pago-subgroup">
                                                            <span className="subgroup-label">ESTADO TRAS EL PAGO</span>
                                                            <div className="subgroup-items">
                                                                {Number(pago.adeudoActual || pago.adeudoNuevo || 0) <= 0 ? (
                                                                    <div className="subgroup-item">
                                                                        <span>Cobrado:</span>
                                                                        <strong className="text-green">
                                                                            {formatCurrency(pago.monto || pago.cargoDelMes || 0)}
                                                                        </strong>
                                                                    </div>
                                                                ) : (
                                                                    <div className="subgroup-item">
                                                                        <span>Adeudo:</span>
                                                                        <strong className="text-red">
                                                                            {formatCurrency(pago.adeudoActual || pago.adeudoNuevo || 0)}
                                                                        </strong>
                                                                    </div>
                                                                )}
                                                                <div className="subgroup-item">
                                                                    <span>Saldo a Favor:</span>
                                                                    <strong className={(pago.saldoFavorActual || pago.saldoFavorNuevo || 0) > 0 ? 'text-green' : ''}>
                                                                        {formatCurrency(pago.saldoFavorActual || pago.saldoFavorNuevo || 0)}
                                                                    </strong>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {(() => {
                                                        const obsExistente = typeof pago.observaciones === 'string' && pago.observaciones.trim() !== '' ? pago.observaciones.trim() : null;
                                                        const esAgua = String(pago.concepto || '').toLowerCase().includes('agua') || String(pago.concepto || '').toLowerCase().includes('cea');
                                                        let textoNota = obsExistente;
                                                        
                                                        if (!textoNota && esAgua) {
                                                            const adeudoPosterior = Number(pago.adeudoActual || pago.adeudoNuevo || 0);
                                                            if (adeudoPosterior > 0) {
                                                                const formattedAdeudo = formatCurrency(adeudoPosterior);
                                                                textoNota = `Pago de cuota de consumo de agua potable liquidado. Cuenta con adeudo histórico pendiente (${formattedAdeudo}). El pago de consumo de agua potable no libera de adeudos anteriores.`;
                                                            } else {
                                                                textoNota = 'Consumo de Agua Potable - AL CORRIENTE';
                                                            }
                                                        }

                                                        if (!textoNota) return null;

                                                        return (
                                                            <div className="pago-notes-accordion-wrapper" style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px solid #f1f5f9', width: '100%' }}>
                                                                <details className="pago-notes-details" style={{ width: '100%' }}>
                                                                    <summary style={{ fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', cursor: 'pointer', outline: 'none', userSelect: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0' }}>
                                                                        <span>NOTAS Y OBSERVACIONES</span>
                                                                        <span style={{ fontSize: '0.7rem', color: '#3b82f6', textTransform: 'none', fontWeight: '600' }}>Ver detalle ▾</span>
                                                                    </summary>
                                                                    <div className="observaciones-box-zen" style={{ marginTop: '6px', padding: '8px 12px', backgroundColor: '#f8fafc', borderRadius: '6px', borderLeft: '3px solid #3b82f6' }}>
                                                                        <p className="observaciones-text" style={{ margin: 0, fontSize: '0.82rem', color: '#334155', fontStyle: 'italic', lineHeight: '1.4', whiteSpace: 'pre-wrap' }}>{textoNota}</p>
                                                                    </div>
                                                                </details>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {pagos.length > 3 && (
                                <button
                                    className="pagos-toggle-btn"
                                    onClick={() => setMostrarTodosPagos((prev) => !prev)}
                                    type="button"
                                >
                                    {mostrarTodosPagos ? (
                                        <>
                                            Ver menos <FaChevronUp />
                                        </>
                                    ) : (
                                        <>
                                            Ver todos los pagos ({pagos.length - 3} más) <FaChevronDown />
                                        </>
                                    )}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Modal / Bottom Sheet Zen: Desglose Detallado de Adeudos */}
            {mostrarModalDesglose && (
                <div className="modal-desglose-overlay" onClick={() => setMostrarModalDesglose(false)}>
                    <div className="modal-desglose-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-desglose-drag-handle" />
                        
                        {/* Header del Modal */}
                        <div className="modal-desglose-header">
                            <div className="modal-desglose-title-box">
                                <div className={`modal-desglose-icon ${tabDesglose}`}>
                                    {tabDesglose === 'mantenimiento' ? <FaDollarSign /> : <FaCalendarAlt />}
                                </div>
                                <div>
                                    <h3 className="modal-desglose-title">Desglose Detallado de Adeudos</h3>
                                    <p className="modal-desglose-subtitle">
                                        Casa {estadoCuenta?.numeroCasa || resolvedCasa.numero || ''} • {estadoCuenta?.propietario || ''}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="modal-desglose-close-btn"
                                onClick={() => setMostrarModalDesglose(false)}
                                title="Cerrar ventana"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Pestañas Zen (Tabs) */}
                        <div className="modal-desglose-tabs">
                            <button
                                type="button"
                                className={`modal-desglose-tab ${tabDesglose === 'mantenimiento' ? 'active' : ''}`}
                                onClick={() => setTabDesglose('mantenimiento')}
                            >
                                <FaDollarSign className="tab-icon" />
                                <span>Mantenimiento ({mesesMantenimientoPendientesList.length} meses)</span>
                            </button>
                            <button
                                type="button"
                                className={`modal-desglose-tab ${tabDesglose === 'extraordinarias' ? 'active' : ''}`}
                                onClick={() => setTabDesglose('extraordinarias')}
                            >
                                <FaCalendarAlt className="tab-icon" />
                                <span>Extraordinarias ({cuotasExtrasDetalladas.length} cuotas)</span>
                            </button>
                        </div>

                        {/* Cuerpo con Scroll Suave */}
                        <div className="modal-desglose-body">
                            {tabDesglose === 'mantenimiento' ? (
                                <div className="desglose-content-pane">
                                    {/* Resumen Zen */}
                                    <div className="desglose-resumen-banner mantto">
                                        <div className="resumen-metric">
                                            <span className="resumen-label">Total Adeudo Mantenimiento</span>
                                            <span className="resumen-val">{formatCurrency(adeudoCapitalMantenimiento)}</span>
                                        </div>
                                        <div className="resumen-divider" />
                                        <div className="resumen-metric">
                                            <span className="resumen-label">Meses Pendientes</span>
                                            <span className="resumen-val-secondary">{mesesMantenimientoPendientesList.length} meses</span>
                                        </div>
                                        <div className="resumen-divider" />
                                        <div className="resumen-metric">
                                            <span className="resumen-label">Años con Adeudo</span>
                                            <span className="resumen-val-secondary">{mantenimientoAgrupadoPorAnio.length} años</span>
                                        </div>
                                    </div>

                                    {/* Acciones de colapso rápido */}
                                    {mantenimientoAgrupadoPorAnio.length > 1 && (
                                        <div className="desglose-quick-actions">
                                            <span className="quick-actions-hint">Presiona un año para expandir o colapsar sus meses:</span>
                                            <div className="quick-actions-btns">
                                                <button
                                                    type="button"
                                                    className="btn-quick-text"
                                                    onClick={() => {
                                                        const expandAll = {};
                                                        mantenimientoAgrupadoPorAnio.forEach(g => expandAll[g.anio] = true);
                                                        setAniosExpandidos(expandAll);
                                                    }}
                                                >
                                                    Expandir todos
                                                </button>
                                                <span>•</span>
                                                <button
                                                    type="button"
                                                    className="btn-quick-text"
                                                    onClick={() => setAniosExpandidos({})}
                                                >
                                                    Colapsar todos
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Lista de Años y Meses */}
                                    {mantenimientoAgrupadoPorAnio.length === 0 ? (
                                        <div className="desglose-empty-state">
                                            <FaInfoCircle className="empty-icon" />
                                            <p>No se registran meses pendientes de mantenimiento para esta vivienda.</p>
                                        </div>
                                    ) : (
                                        <div className="desglose-anios-accordion">
                                            {mantenimientoAgrupadoPorAnio.map((grupo) => {
                                                const estaAbierto = aniosExpandidos[grupo.anio] !== false; // Abierto por defecto
                                                return (
                                                    <div key={grupo.anio} className="anio-accordion-card">
                                                        <div
                                                            className="anio-accordion-header"
                                                            onClick={() => setAniosExpandidos(prev => ({
                                                                ...prev,
                                                                [grupo.anio]: !estaAbierto
                                                            }))}
                                                        >
                                                            <div className="anio-header-left">
                                                                <span className="anio-pill">Año {grupo.anio}</span>
                                                                <span className="anio-meses-count">
                                                                    {grupo.meses.length} {grupo.meses.length === 1 ? 'mes' : 'meses'} pendientes
                                                                </span>
                                                            </div>
                                                            <div className="anio-header-right">
                                                                <span className="anio-monto-total">{formatCurrency(grupo.totalMontoPendiente)}</span>
                                                                <span className={`anio-arrow-icon ${estaAbierto ? 'open' : ''}`}>
                                                                    <FaChevronDown />
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {estaAbierto && (
                                                            <div className="anio-accordion-content">
                                                                <div className="meses-grid-list">
                                                                    {grupo.meses.map((mesItem) => {
                                                                        const tieneAbono = mesItem.montoPagado > 0;
                                                                        return (
                                                                            <div key={mesItem.id} className="mes-card-item">
                                                                                <div className="mes-card-left">
                                                                                    <div className="mes-name-box">
                                                                                        <span className="mes-name-text">{mesItem.mesNombre} {grupo.anio}</span>
                                                                                        {tieneAbono && (
                                                                                            <span className="badge-abono-parcial">
                                                                                                Abono de {formatCurrency(mesItem.montoPagado)}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <span className="mes-original-hint">
                                                                                        Cuota fijada: {formatCurrency(mesItem.montoOriginal)}
                                                                                    </span>
                                                                                </div>
                                                                                <div className="mes-card-right">
                                                                                    <span className="mes-monto-pendiente">
                                                                                        {formatCurrency(mesItem.montoPendiente)}
                                                                                    </span>
                                                                                    <span className={`mes-status-tag ${mesItem.estado === 'pagado_parcialmente' ? 'parcial' : 'pendiente'}`}>
                                                                                        {mesItem.estado === 'pagado_parcialmente' ? 'Abono Parcial' : 'Pendiente'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="desglose-content-pane">
                                    {/* Resumen Zen Extraordinarias */}
                                    <div className="desglose-resumen-banner extras">
                                        <div className="resumen-metric">
                                            <span className="resumen-label">Total Cuotas Extraordinarias</span>
                                            <span className="resumen-val">{formatCurrency(totalCuotasExtrasPendientes)}</span>
                                        </div>
                                        <div className="resumen-divider" />
                                        <div className="resumen-metric">
                                            <span className="resumen-label">Cuotas Pendientes</span>
                                            <span className="resumen-val-secondary">{cuotasExtrasDetalladas.length} cuotas</span>
                                        </div>
                                    </div>

                                    {cuotasExtrasDetalladas.length === 0 ? (
                                        <div className="desglose-empty-state">
                                            <FaInfoCircle className="empty-icon" />
                                            <p>No se registran cuotas extraordinarias pendientes para esta vivienda.</p>
                                        </div>
                                    ) : (
                                        <div className="cuotas-extras-detailed-list">
                                            {cuotasExtrasDetalladas.map((cuota, idx) => {
                                                const tieneAbono = cuota.montoPagado > 0;
                                                return (
                                                    <div key={cuota.id || idx} className="cuota-extra-detailed-card">
                                                        <div className="cuota-extra-top">
                                                            <div className="cuota-extra-info">
                                                                <span className="cuota-extra-badge-idx">{idx + 1}</span>
                                                                <div>
                                                                    <h4 className="cuota-extra-title">{cuota.concepto}</h4>
                                                                    {cuota.anio && (
                                                                        <span className="cuota-extra-periodo">Periodo anual: {cuota.anio}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="cuota-extra-amounts">
                                                                <span className="cuota-extra-pending-amount">
                                                                    {formatCurrency(cuota.montoPendiente)}
                                                                </span>
                                                                <span className={`cuota-extra-status-pill ${cuota.estado === 'pagado_parcial' ? 'parcial' : 'pendiente'}`}>
                                                                    {cuota.estado === 'pagado_parcial' ? 'Abono Parcial' : 'Pendiente'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        <div className="cuota-extra-bottom">
                                                            <span className="cuota-extra-detail-meta">
                                                                Monto de la cuota: <strong>{formatCurrency(cuota.montoOriginal)}</strong>
                                                            </span>
                                                            {tieneAbono && (
                                                                <span className="cuota-extra-detail-meta abono">
                                                                    Abonado a la fecha: <strong>{formatCurrency(cuota.montoPagado)}</strong>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer Zen */}
                        <div className="modal-desglose-footer">
                            <button
                                type="button"
                                className="btn-modal-desglose-cerrar"
                                onClick={() => setMostrarModalDesglose(false)}
                            >
                                Entendido / Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Recibo Oficial para el Residente */}
            {showReceiptModal && reciboSeleccionado && (
                <div className="modal-overlay receipt-modal" onClick={() => setShowReceiptModal(false)}>
                    <div className="modal-content receipt-modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setShowReceiptModal(false)} title="Cerrar recibo">✕</button>
                        <div className="receipt-scroll-area">
                            <div className="receipt-preview-container">
                                <div className="preview-wrapper" ref={receiptRef}>
                                    {renderReceipt(reciboSeleccionado)}
                                </div>
                            </div>
                        </div>
                        <div className="receipt-actions">
                            <button type="button" className="btn-secondary btn-download" onClick={handleDownloadPrint}>
                                ⬇️ Descargar / Imprimir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ModalAvisoMorosidadFinanzas
                isOpen={Boolean(debeMostrarAvisoFinanzas && !avisoDismissed)}
                onClose={() => setAvisoDismissed(true)}
                adeudo={totalAPagarFinDeMes > 0 ? totalAPagarFinDeMes : (estadoCuenta?.adeudoReal || userDebt?.adeudo || estadoCuenta?.adeudo || 0)}
                numeroCasa={resolvedCasa?.numero || numeroCasa || ''}
            />

            {/* Modal para Subir Comprobante de Pago */}
            {modulos?.asistente_virtual_ia !== false && modulos?.comunidad !== false && modalSubirComprobanteOpen && (
                <div className="modal-comprobante-overlay" onClick={() => !subiendoComprobante && setModalSubirComprobanteOpen(false)}>
                    <div className="modal-comprobante-card" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-comprobante-header">
                            <div className="modal-comprobante-title-group">
                                <div className="modal-comprobante-icon-wrap">
                                    <FaUpload />
                                </div>
                                <div>
                                    <h3>Subir Comprobante de Pago</h3>
                                    <p>Casa {estadoCuenta?.numeroCasa || userData?.casa} • {estadoCuenta?.propietario || userData?.nombre}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="modal-comprobante-close"
                                onClick={() => !subiendoComprobante && setModalSubirComprobanteOpen(false)}
                                disabled={subiendoComprobante}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <form onSubmit={handleSubirComprobante} className="modal-comprobante-body">
                            <div className="comprobante-dropzone">
                                <input
                                    type="file"
                                    id="input-file-comprobante-finanzas"
                                    accept="image/*,application/pdf"
                                    onChange={handleArchivoChange}
                                    style={{ display: 'none' }}
                                    disabled={subiendoComprobante}
                                />
                                <label htmlFor="input-file-comprobante-finanzas" className="comprobante-dropzone-label">
                                    {previewComprobante ? (
                                        <div className="comprobante-preview-wrap">
                                            <img src={previewComprobante} alt="Preview comprobante" className="comprobante-preview-img" />
                                            <span className="comprobante-change-text">Clic para cambiar imagen</span>
                                        </div>
                                    ) : archivoComprobante ? (
                                        <div className="comprobante-file-selected">
                                            <FaReceipt className="file-icon" />
                                            <span>{archivoComprobante.name}</span>
                                            <span className="comprobante-change-text">Clic para cambiar archivo</span>
                                        </div>
                                    ) : (
                                        <div className="comprobante-placeholder">
                                            <FaUpload className="upload-icon-large" />
                                            <span className="upload-primary-text">Selecciona o toma foto de tu comprobante</span>
                                            <span className="upload-sub-text">Formatos permitidos: JPG, PNG, PDF (Máx. 10MB)</span>
                                        </div>
                                    )}
                                </label>
                            </div>

                            <div className="form-group-comprobante">
                                <label>Monto pagado (MXN - Opcional):</label>
                                <div className="input-with-icon">
                                    <span className="currency-prefix">$</span>
                                    <input
                                        type="number"
                                        step="any"
                                        placeholder={estadoCuenta?.adeudo ? String(estadoCuenta.adeudo) : "0.00"}
                                        value={montoComprobante}
                                        onChange={(e) => setMontoComprobante(e.target.value)}
                                        disabled={subiendoComprobante}
                                    />
                                </div>
                            </div>

                            <div className="form-group-comprobante">
                                <label>Notas o Referencia (Opcional):</label>
                                <textarea
                                    rows="2"
                                    placeholder="Ej. Transferencia SPEI Bancomer, ref 891234..."
                                    value={notasComprobante}
                                    onChange={(e) => setNotasComprobante(e.target.value)}
                                    disabled={subiendoComprobante}
                                />
                            </div>

                            <div className="modal-comprobante-actions">
                                <button
                                    type="button"
                                    className="btn-cancelar-comprobante"
                                    onClick={() => setModalSubirComprobanteOpen(false)}
                                    disabled={subiendoComprobante}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-enviar-comprobante"
                                    disabled={subiendoComprobante || !archivoComprobante}
                                >
                                    {subiendoComprobante ? (
                                        <>
                                            <FaSpinner className="spin-icon" /> Subiendo comprobante...
                                        </>
                                    ) : (
                                        <>
                                            <FaUpload /> Enviar Comprobante
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanzasUsuario;
